import { useState, useEffect, useCallback, useRef, type CSSProperties, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiFetch, ApiError } from '../../lib/api';
import { SOURCE_META, CONVERSATION_SOURCES, OAUTH_SOURCES, TEAM_SHARED_OAUTH_SOURCES, buildSources, sourceName, captureStatusChip, sourceSortRank, isTeamAdmin as memberIsTeamAdmin } from '../../lib/sources';
import { buildSourceActionBody } from '../../lib/source-actions';
import { SourceIcon } from '../shared/SourceIcon';
import { ClaudeCodeInstall } from '../shared/ClaudeCodeInstall';
import { Drawer } from '../shared/Drawer';
import { connectChatSource, triggerBackfill } from '../../lib/connect-source';
import { relativeTime } from '../../lib/formatting';
import type { Source, SourceAccount } from '../../lib/sources';
import { useExtensionStatus } from '../../hooks/useExtensionStatus';
import { useOAuthPopup } from '../../hooks/useOAuthPopup';
import { useEscapeToClose } from '../../hooks/useEscapeToClose';
import { useIsMobile } from '../../hooks/useIsMobile';
import { ConnectOnDesktopSheet } from '../shared/ConnectOnDesktopSheet';
import { useTeamSettings, useCurrentUserId } from '../../hooks/useTeamSettings';
import { sourcesSurfaceQueryKey } from '../../hooks/useSourcesSurface';
import type { SourcesSurfaceData } from '../../hooks/useSourcesSurface';
import { CHROME_STORE_URL } from '../../lib/constants';
import { track } from '../../lib/analytics';
import { PageMast, MastTile } from '../shared/workroom';

interface SourcesSurfaceProps {
  data: SourcesSurfaceData;
  onNav: (id: string) => void;
  // The signed-in user's email. Google/Slack have no per-source `source_account.email`,
  // so on an OAuth-connect success we confirm against the account that's signed in (req B).
  userEmail?: string;
}

interface CaptureProgress {
  status: string;
  fetched: number;
  estimated: number;
  totalConversations: number;
  conversationsProcessed: number;
  lastConversationTitle: string | null;
}

// One-line "what connecting captures" line shown on each source card's `.sprivacy` row
// (info-first: a connected chat source instead shows the bound account).
const SOURCE_DESCRIPTIONS: Record<string, string> = {
  chatgpt: 'Captures your ChatGPT prompts and topic clusters.',
  claude: 'Captures your Claude reasoning style and recurring asks.',
  perplexity: 'Captures your Perplexity research patterns and questions.',
  'claude-code': 'Captures CLI coding sessions via the Stop hook.',
  google: 'Read-only mirror of Gmail, Google Docs + Calendar.',
  slack: 'Public-channel messages only. Also delivers your daily briefing.',
  slack_user: 'Your own DMs & private channels — distilled privately, just for you.',
  notion: 'Read-only mirror of the pages & databases your team shares with Terum.',
  granola: 'Read-only mirror of your Granola meetings — topics, decisions & who participated.',
};

// Sources whose distilled data lands in the team Knowledge list (/map · List), with the
// per-source verb for the "View …" action in the Manage drawer. Each maps to the `site`
// value the Knowledge list filters on. Excluded on purpose:
//   • slack (workspace bot) — store-only, never compacted → nothing in Knowledge.
//   • slack_user (your DMs)  — distilled PRIVATE (owner-only) → excluded from the team Map.
//   • github                 — team-owned rows show on the Map graph (bug-312) but not this
//     list route yet (it filters by user_id; team-owned rows are user_id-NULL).
const KNOWLEDGE_VIEW: Record<string, { site: string; label: string }> = {
  chatgpt: { site: 'chatgpt', label: 'View conversations' },
  claude: { site: 'claude', label: 'View conversations' },
  perplexity: { site: 'perplexity', label: 'View conversations' },
  'claude-code': { site: 'claude-code', label: 'View sessions' },
  google: { site: 'google-docs', label: 'View documents' },
  // Granola topics compact into the knowledge graph (site='granola'), so they land in the list.
  granola: { site: 'granola', label: 'View meetings' },
};

// Shared centered-dialog scaffolding (warm overhaul tokens). D10: only the disconnect /
// delete-all confirmations and the post-connect confirmation stay centered dialogs — every
// other panel is a right-side drawer.
const DIALOG_BACKDROP: CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(10,8,4,.36)', backdropFilter: 'blur(2px)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 90,
};
const DIALOG_CARD: CSSProperties = {
  position: 'relative', background: 'var(--panel)', border: '1px solid var(--hair2)',
  borderRadius: 14, width: '90%', maxWidth: 420, padding: 24, boxShadow: 'var(--sig-shadow)',
};
const ROSE_BTN: CSSProperties = {
  color: 'var(--rose)', borderColor: 'color-mix(in srgb, var(--rose) 40%, transparent)',
};
const AMBER_BTN: CSSProperties = {
  color: 'var(--amb)', borderColor: 'color-mix(in srgb, var(--amb) 40%, transparent)',
};

// Neutral shield glyph for the d-connect drawer header (mock §d-connect).
const SHIELD_ICON = (
  <span className="ltile" style={{ width: 36, height: 36, background: 'var(--acc-soft)', borderColor: 'color-mix(in srgb, var(--acc) 30%, transparent)', color: 'var(--acc)' }}>
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
      <path d="M12 3l7 3v5c0 4.4-3 7.6-7 9-4-1.4-7-4.6-7-9V6l7-3z" />
    </svg>
  </span>
);

// Post-connect confirmation dialog. Shown after a source connects, naming the account.
export function ConnectedAccountDialog({ email, onClose }: { email: string; onClose: () => void }) {
  return (
    <div onClick={onClose} style={DIALOG_BACKDROP}>
      <div onClick={e => e.stopPropagation()} style={DIALOG_CARD}>
        <button onClick={onClose} aria-label="Close" style={{
          position: 'absolute', top: 12, right: 14, background: 'none', border: 'none',
          color: 'var(--inkS)', fontSize: 22, lineHeight: 1, cursor: 'pointer', padding: 0,
        }}>×</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <span style={{ fontSize: 22, color: 'var(--grn)' }}>✓</span>
          <div style={{ fontSize: 17, fontWeight: 600, color: 'var(--inkB)' }}>Now connected</div>
        </div>
        <div style={{ fontSize: 13, color: 'var(--inkS)', lineHeight: 1.55, marginBottom: 20 }}>
          Your account is now connected to <span style={{ color: 'var(--inkB)', fontWeight: 600 }}>{email}</span>.
        </div>
        <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} onClick={onClose}>Done</button>
      </div>
    </div>
  );
}

// Per-source Remove confirmation (a full revoke: wipe data + drop consent). D10 keeps this a
// small confirm dialog rather than a drawer.
function RemoveConfirmDialog({ sourceId, loading, onConfirm, onCancel }: {
  sourceId: string | null;
  loading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  // This confirm opens OVER the manage drawer, which stays mounted so Cancel can return to it. It
  // must therefore own Escape while it is up, or Esc would close the drawer underneath and leave
  // this dialog floating over the bare grid. Inert while the request is in flight, matching the
  // disabled Cancel/✕ below.
  useEscapeToClose(onCancel, !!sourceId && !loading);
  if (!sourceId) return null;
  const meta = SOURCE_META[sourceId] || { name: sourceId, trustCopy: '', signal: '' };
  return (
    <div onClick={loading ? undefined : onCancel} style={DIALOG_BACKDROP}>
      <div onClick={e => e.stopPropagation()} style={DIALOG_CARD}>
        <button onClick={onCancel} disabled={loading} aria-label="Close" style={{
          position: 'absolute', top: 12, right: 14, background: 'none', border: 'none',
          color: 'var(--inkS)', fontSize: 22, lineHeight: 1, cursor: loading ? 'default' : 'pointer', padding: 0,
        }}>×</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          <SourceIcon id={sourceId} size={30} />
          <div style={{ fontSize: 17, fontWeight: 600, color: 'var(--inkB)' }}>Remove {meta.name}</div>
        </div>
        <div style={{ fontSize: 13, color: 'var(--inkS)', lineHeight: 1.55, marginBottom: 20 }}>
          This disconnects <span style={{ color: 'var(--inkB)', fontWeight: 600 }}>{meta.name}</span> and deletes its captured data. To stop recording but keep your data, use <span style={{ color: 'var(--inkB)', fontWeight: 600 }}>Pause</span> instead.
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-secondary" disabled={loading} style={{ flex: 1, justifyContent: 'center' }} onClick={onCancel}>Cancel</button>
          <button className="btn btn-secondary" disabled={loading} style={{ flex: 1, justifyContent: 'center', ...ROSE_BTN, opacity: loading ? 0.6 : 1 }} onClick={onConfirm}>
            {loading ? 'Removing…' : 'Remove'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Per-source Pause confirmation — same scaffolding as RemoveConfirmDialog (centered dialog,
// Cancel + colored confirm), amber instead of rose since pausing keeps the data. OAuth sources
// (google/slack/slack_user/github/notion) pause by revoking their token, so resuming them costs
// more than resuming a chat source (a fresh reconnect, not a one-click resume) — the copy says
// so plainly rather than letting that surprise show up only after the click.
function PauseConfirmDialog({ sourceId, loading, onConfirm, onCancel }: {
  sourceId: string | null;
  loading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  // Same as RemoveConfirmDialog: it opens over the still-mounted manage drawer, so it has to be the
  // one Escape dismisses.
  useEscapeToClose(onCancel, !!sourceId && !loading);
  if (!sourceId) return null;
  const meta = SOURCE_META[sourceId] || { name: sourceId, trustCopy: '', signal: '' };
  const isOAuth = OAUTH_SOURCES.includes(sourceId);
  const isTeamShared = TEAM_SHARED_OAUTH_SOURCES.includes(sourceId);
  return (
    <div onClick={loading ? undefined : onCancel} style={DIALOG_BACKDROP}>
      <div onClick={e => e.stopPropagation()} style={{ ...DIALOG_CARD, borderColor: 'color-mix(in srgb, var(--amb) 30%, transparent)' }}>
        <button onClick={onCancel} disabled={loading} aria-label="Close" style={{
          position: 'absolute', top: 12, right: 14, background: 'none', border: 'none',
          color: 'var(--inkS)', fontSize: 22, lineHeight: 1, cursor: loading ? 'default' : 'pointer', padding: 0,
        }}>×</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          <SourceIcon id={sourceId} size={30} />
          <div style={{ fontSize: 17, fontWeight: 600, color: 'var(--inkB)' }}>Pause {meta.name}</div>
        </div>
        <div style={{ fontSize: 13, color: 'var(--inkS)', lineHeight: 1.55, marginBottom: isTeamShared ? 14 : 20 }}>
          Stops new {meta.name} data from coming in — everything already captured stays.{' '}
          {isOAuth
            ? <>Terum revokes its access when you pause, so resuming means reconnecting {meta.name} from scratch — there's no quick undo.</>
            : <>You can resume any time.</>}
        </div>
        {isTeamShared && (
          <div style={{ fontSize: 12.5, color: 'var(--rose)', lineHeight: 1.55, padding: '11px 13px', background: 'color-mix(in srgb, var(--rose) 8%, transparent)', border: '1px solid color-mix(in srgb, var(--rose) 28%, transparent)', borderRadius: 9, marginBottom: 20 }}>
            {meta.name} is connected once for your whole team — pausing it stops {sourceId === 'slack' ? 'everyone’s daily briefing delivery' : 'ingestion of your team’s shared knowledge'}, not just yours.
          </div>
        )}
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-secondary" disabled={loading} style={{ flex: 1, justifyContent: 'center' }} onClick={onCancel}>Cancel</button>
          <button className="btn btn-secondary" disabled={loading} style={{ flex: 1, justifyContent: 'center', ...AMBER_BTN, opacity: loading ? 0.6 : 1 }} onClick={onConfirm}>
            {loading ? 'Pausing…' : 'Pause'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Top-right "Delete all data" kill switch: wipes everything AND pauses incoming capture.
// Destructive, so backdrop/Cancel default to keeping data — only the explicit confirm proceeds.
function DeleteAllDialog({ open, loading, onConfirm, onCancel }: {
  open: boolean;
  loading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  if (!open) return null;
  return (
    <div onClick={loading ? undefined : onCancel} style={DIALOG_BACKDROP}>
      <div onClick={e => e.stopPropagation()} style={{ ...DIALOG_CARD, maxWidth: 440, borderColor: 'color-mix(in srgb, var(--rose) 30%, transparent)' }}>
        <button onClick={onCancel} disabled={loading} aria-label="Close" style={{
          position: 'absolute', top: 12, right: 14, background: 'none', border: 'none',
          color: 'var(--inkS)', fontSize: 22, lineHeight: 1, cursor: loading ? 'default' : 'pointer', padding: 0,
        }}>×</button>
        <div style={{ fontSize: 17, fontWeight: 600, color: 'var(--rose)', marginBottom: 14 }}>Delete all data</div>
        <div style={{ fontSize: 13, color: 'var(--inkS)', lineHeight: 1.6, marginBottom: 22 }}>
          This will delete all your saved data and stop capture on all incoming data. To deactivate or delete data from any one source, use that source's Manage drawer.
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button className="btn btn-secondary" disabled={loading} style={{ width: '100%', justifyContent: 'center', ...ROSE_BTN, opacity: loading ? 0.6 : 1 }} onClick={onConfirm}>
            {loading ? 'Deleting…' : 'Confirm, I want to delete all data.'}
          </button>
          <button className="btn btn-secondary" disabled={loading} style={{ width: '100%', justifyContent: 'center' }} onClick={onCancel}>Cancel, I want to keep my data.</button>
        </div>
      </div>
    </div>
  );
}

// Live state derivation for one source — the same predicate the card and the manage drawer
// both read so the badge and the drawer never disagree. bug-197/198/280: CAPTURING is the
// live extension snapshot 'running' OR the durable backfill flag OR still-compacting.
function deriveCapturing(source: Source, captureProgress: CaptureProgress | null, stale: boolean, isConversationSource: boolean): boolean {
  return isConversationSource && !stale &&
    (captureProgress?.status === 'running' || source.backfill_in_progress === true || source.processing === true);
}

// Warm dropdown for the Slack team-briefing channel. Replaces a bare native <select> (the
// `.di` input style is scoped to `.drawer`, so on a card the select rendered unstyled and
// clashed with the rest of the surface). Mirrors the MapControls dropdown: a chip button +
// a popover menu, all warm tokens.
function TeamChannelPicker({ channels, selectedId, disabled, onSelect }: {
  channels: Array<{ id: string; name?: string | null }>;
  selectedId: string | null;
  disabled: boolean;
  onSelect: (channelId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const selected = channels.find(c => c.id === selectedId) ?? null;
  return (
    <div ref={ref} style={{ position: 'relative', flex: 1, minWidth: 0 }}>
      <button
        type="button"
        className="chip"
        disabled={disabled}
        onClick={() => setOpen(o => !o)}
        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, opacity: disabled ? 0.6 : 1 }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: selected ? 'var(--inkB)' : 'var(--faint)' }}>
          {selected ? `#${selected.name ?? selected.id}` : 'Select a channel…'}
        </span>
        <svg width="8" height="5" viewBox="0 0 8 5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <path d="M1 1L4 4L7 1" />
        </svg>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 5px)', left: 0, right: 0, zIndex: 30,
          background: 'var(--panel)', border: '1px solid var(--hair2)', borderRadius: 9,
          padding: 4, boxShadow: 'var(--sig-shadow)', maxHeight: 220, overflowY: 'auto',
          display: 'flex', flexDirection: 'column', gap: 1,
        }}>
          {channels.map(ch => (
            <button
              key={ch.id}
              type="button"
              onClick={() => { onSelect(ch.id); setOpen(false); }}
              style={{
                textAlign: 'left', border: 'none', borderRadius: 6, padding: '7px 10px', cursor: 'pointer',
                fontSize: 12, fontFamily: "'DM Sans', sans-serif",
                background: ch.id === selectedId ? 'var(--acc-soft)' : 'transparent',
                color: ch.id === selectedId ? 'var(--inkB)' : 'var(--ink)',
              }}
            >
              #{ch.name ?? ch.id}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// A single source card in the unified grid (redesign port — prototype sources-page anatomy).
// Info-first: status headline, a state-specific middle block (CAPTURING progress / drift /
// metrics), the per-source privacy line, and one footer action that opens the right drawer.
// All management lives in the drawers. Every state branch is unchanged from the pre-redesign
// card — only the markup vocabulary moved to Tailwind under the `.rd` root.

// Shared card vocabulary (kept as constants so the two card variants can't drift).
const CARD_CLS = 'flex flex-col rounded-[24px] border bg-card p-5 shadow-[0_16px_34px_rgba(88,69,42,.055)]';
const STAT_CLS = 'mt-2 text-[13px] leading-5 text-muted-foreground';
const PILL_CLS = 'rounded-full border border-[#d7cab7] bg-[#fffaf2] px-3.5 py-1 text-sm transition hover:border-foreground hover:bg-foreground hover:text-card disabled:cursor-default disabled:opacity-60 dark:border-border dark:bg-card';
const PILL_PRIMARY_CLS = 'rounded-full bg-foreground px-3.5 py-1 text-sm text-card transition hover:opacity-90 disabled:cursor-default disabled:opacity-60';

function StatusChipLine({ color, pulse, label, tail }: { color: string; pulse?: boolean; label: string; tail?: ReactNode }) {
  return (
    <p className="mt-0.5 flex items-center gap-1.5 font-['DM_Mono'] text-[9px] uppercase tracking-[.1em]" style={{ color }}>
      <span className={`size-1.5 shrink-0 rounded-full${pulse ? ' animate-pulse' : ''}`} style={{ backgroundColor: color }} />
      {label}
      {tail}
    </p>
  );
}

function Metric({ value, label }: { value: string; label: string }) {
  return (
    <span className="text-[13px] text-muted-foreground">
      <b className="mr-1 font-semibold text-foreground">{value}</b>{label}
    </span>
  );
}

function SourceCard({
  source, captureProgress, stale, isConversationSource, capturing,
  onManage, onActivate, onReconnect, onSetTeamChannel, onChooseRepos, onChooseTeams, actionLoading, isMobile, isTeamAdmin,
}: {
  source: Source;
  captureProgress: CaptureProgress | null;
  stale: boolean;
  isConversationSource: boolean;
  capturing: boolean;
  onManage: (id: string) => void;
  onActivate: (id: string) => void;
  onReconnect: (id: string) => void;
  onSetTeamChannel: (id: string, channelId: string) => void;
  onChooseRepos: (id: string) => void;
  onChooseTeams: (id: string) => void;
  actionLoading: string | null;
  // On a phone the card is read-only status: no footer actions, no channel/repo pickers.
  // Everything that connects, reconnects, pauses, or deletes lives on the desktop app.
  isMobile: boolean;
  // bug-395: is the viewer a team admin. Team-shared sources (slack/notion) are admin-only to
  // connect/manage — non-admins get a read-only status card instead of a dead Activate.
  isTeamAdmin: boolean;
}) {
  const drifted = source.status === 'needs-attention';
  const signedOut = source.status === 'signed-out';
  const isPaused = source.status === 'paused';
  const isActive = source.status === 'active';
  const isNotActive = source.status === 'not-active';
  const account = source.source_account ?? null;
  const isLoading = actionLoading === source.id;

  // Status headline — the ONE shared capture-status chip (spec §7) drives it for EVERY source, so
  // OAuth/mirror sources (google/slack/notion/github/linear/granola) finally get the live signal
  // chat/CLI already had (syncing / stale-live / needs-attention / error), not just ACTIVE/NOT ACTIVE.
  // Two chat-specific real-time signals capture_status doesn't encode stay as overrides on top:
  //   • `capturing` — the extension's live import progress (more immediate than the backend's view).
  //   • `drifted`   — a different account is signed in (amber "PAUSED · account changed"; detail below).
  // Falls back to the legacy status label when capture_status is absent (claude-code has no
  // integration row → its chip stays device-status driven).
  const legacyChip = {
    label: signedOut ? 'NOT SIGNED IN' : isPaused ? 'PAUSED' : isActive ? 'ACTIVE' : 'NOT ACTIVE',
    color: (signedOut || isPaused) ? 'var(--amb)' : isActive ? 'var(--grn)' : 'var(--rose)',
    pulse: false,
  };
  const chip = capturing
    ? { label: 'CAPTURING', color: 'var(--acc)', pulse: true }
    : drifted
      ? { label: 'PAUSED · account changed', color: 'var(--amb)', pulse: false }
      : (captureStatusChip(source.capture_status) ?? legacyChip);

  // Recency phrase shown after the status for live sources.
  const recency = OAUTH_SOURCES.includes(source.id)
    ? (source.last_synced_at ? `synced ${relativeTime(source.last_synced_at)}` : 'awaiting first sync')
    : (source.last_activity ? relativeTime(source.last_activity) : 'no activity yet');

  const boundAccount = account?.email || account?.label || null;

  // Card border tints amber on drift OR signed-out (bug-373) to draw the eye to the one
  // that needs action.
  const borderCls = (drifted || signedOut) ? 'border-[#e0c9a0]' : 'border-border';

  // bug-395: a team-shared OAuth source (slack/notion) is connected ONCE by a team admin for the
  // whole team. A non-admin can't connect or manage it (the connect route is admin-gated → 403),
  // and the team may already have it connected — so render a read-only STATUS card, never an
  // actionable (always-403) Activate or a mutating Manage. "Connected · managed by your Terum team
  // admin" (a team can have multiple admins, so the copy is generic, never a name). Admins fall
  // through to the full card below unchanged.
  if (TEAM_SHARED_OAUTH_SOURCES.includes(source.id) && !isTeamAdmin) {
    const teamConnected = source.team_connected === true;
    // The shared chip renders `managed` NEUTRAL (var(--faint)), never a green health tone — a
    // non-owner can't observe this team-shared source's capture health, so the chip must not assert
    // one (spec §3a/§8). The backend sends capture_status:'managed' for a team-connected source; the
    // fallback derives the same neutral CONNECTED from team_connected directly, so the card is robust
    // even on an edge payload without capture_status (and shows rose "NOT ACTIVE" when the team hasn't
    // connected it).
    const mChip = captureStatusChip(source.capture_status) ?? {
      label: teamConnected ? 'CONNECTED' : 'NOT ACTIVE',
      color: teamConnected ? 'var(--faint)' : 'var(--rose)',
      pulse: false,
    };
    return (
      <article className={`${CARD_CLS} border-border`}>
        <div className="flex items-center gap-3">
          <span className={`grid size-10 shrink-0 place-items-center rounded-xl bg-muted ${teamConnected ? '' : 'opacity-55'}`}>
            <SourceIcon id={source.id} size={22} />
          </span>
          <div className="min-w-0">
            <p className="text-[15px] font-semibold leading-5">{source.name}</p>
            <StatusChipLine color={mChip.color} pulse={mChip.pulse} label={mChip.label} />
          </div>
        </div>
        <p className={STAT_CLS}>
          {teamConnected
            ? 'Connected · managed by your Terum team admin.'
            : `Only Terum team admins can connect ${source.name}.`}
        </p>
        <p className="mt-2 flex-1 text-[12px] leading-4 text-muted-foreground/80">
          {SOURCE_DESCRIPTIONS[source.id] ?? SOURCE_META[source.id]?.signal ?? ''}
        </p>
      </article>
    );
  }

  return (
    <article className={`${CARD_CLS} ${borderCls}`}>
      <div className="flex items-center gap-3">
        <span className={`grid size-10 shrink-0 place-items-center rounded-xl bg-muted ${isNotActive ? 'opacity-55' : ''}`}>
          <SourceIcon id={source.id} size={22} />
        </span>
        <div className="min-w-0">
          <p className="text-[15px] font-semibold leading-5">{source.name}</p>
          <StatusChipLine
            color={chip.color}
            pulse={chip.pulse}
            label={chip.label}
            tail={
              /* bug-409: gate recency on the chip actually being `live` (a stale-live still shows it —
                 "STALE · synced 2h ago" is informative) so it never reads next to a SYNCING/ERROR/
                 NEEDS-ATTENTION chip. Fall back to isActive when there's no capture_status (claude-code). */
              !capturing && (source.capture_status ? source.capture_status.state === 'live' : isActive)
                ? <span className="normal-case tracking-normal text-muted-foreground">· {recency}</span>
                : undefined
            }
          />
        </div>
      </div>

      {/* Middle block — varies by state. */}
      {capturing ? (
        <div className="mt-3">
          {captureProgress && captureProgress.totalConversations > 0 && (
            <div className="h-1.5 overflow-hidden rounded-full bg-muted">
              <i className="block h-full rounded-full bg-acc" style={{ width: `${Math.min(Math.max((captureProgress.conversationsProcessed / captureProgress.totalConversations) * 100, 2), 99)}%` }} />
            </div>
          )}
          <p className="mt-2 text-[13px] text-muted-foreground">
            {captureProgress && captureProgress.totalConversations > 0
              ? `${captureProgress.conversationsProcessed.toLocaleString()} / ${captureProgress.totalConversations.toLocaleString()} conversations · ${captureProgress.fetched.toLocaleString()} turns`
              : captureProgress && captureProgress.fetched > 0
                ? `${captureProgress.fetched.toLocaleString()} turns imported`
                : 'Starting import…'}
          </p>
          {captureProgress?.lastConversationTitle && (
            <p className="mt-1 truncate font-['DM_Mono'] text-[11px] text-muted-foreground">
              "{captureProgress.lastConversationTitle}" · just now
            </p>
          )}
        </div>
      ) : drifted ? (
        // Unit A: neutral framing. A second signed-in account is a normal thing to do, so the
        // card states the two accounts and the (still true) paused status without alarm wording,
        // and the footer offers a choice rather than a single "fix this" exit.
        <p className="mt-3 rounded-xl border border-[#e0c9a0] bg-[#f9f0dc] px-3 py-2 text-[12px] leading-4 text-[#8a6a33] dark:bg-transparent dark:text-[#c99b52]">
          {source.name} is signed in as <b>{describeObservedAccount(account)}</b>, and Terum is set up to capture <b>{boundAccount || 'the account you connected'}</b>. Capture is paused until you choose.
        </p>
      ) : signedOut ? (
        // bug-373: connected but no account signed into the browser → can't capture. Two
        // distinct cases (kept separate by whether an account was ever bound): never signed
        // in since connect, vs signed in once and later logged out.
        <p className="mt-3 rounded-xl border border-[#e0c9a0] bg-[#f9f0dc] px-3 py-2 text-[12px] leading-4 text-[#8a6a33] dark:bg-transparent dark:text-[#c99b52]">
          {boundAccount
            ? <>Signed out of <b>{boundAccount}</b> — sign back in to {source.name} in your browser to resume capturing.</>
            : <>Not signed in — sign in to {source.name} in your browser to start capturing.</>}
          {source.last_identity_check_at && (
            <span className="opacity-70"> · checked {relativeTime(source.last_identity_check_at)}</span>
          )}
        </p>
      ) : source.id === 'google' ? (
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
          <Metric value={(source.item_counts?.emails ?? 0).toLocaleString()} label="emails" />
          <Metric value={(source.item_counts?.docs ?? 0).toLocaleString()} label="docs" />
          <Metric value={(source.item_counts?.calendar ?? 0).toLocaleString()} label="meetings" />
        </div>
      ) : source.id === 'slack' ? (
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
          <Metric value={(source.message_count ?? 0).toLocaleString()} label="messages" />
          <Metric value={(source.channel_count ?? 0).toLocaleString()} label="channels" />
        </div>
      ) : source.id === 'slack_user' ? (
        // tier-3 has no live extension progress + no message count in the surface payload,
        // so render an honest indeterminate "importing" while the DM backfill runs, else a
        // privacy reassurance. The partial banner below carries the no-silent-degradation count.
        isActive && !source.backfill_complete ? (
          <p className="mt-3 flex items-center gap-1.5 text-[13px] text-muted-foreground">
            <span className="size-1.5 animate-pulse rounded-full bg-acc" /> Importing your Slack DMs…
          </p>
        ) : (
          <p className={STAT_CLS}>Private — only you see what's distilled from your DMs.</p>
        )
      ) : source.id === 'github' ? (
        // Connected but the required "pick repositories" step is unfinished → prompt instead of
        // showing 0/0 mirror metrics (nothing is ingested until the user chooses).
        source.repos_pending ? (
          <p className={STAT_CLS}>Connected — choose which repositories Terum ingests to finish setup.</p>
        ) : (
          // bug-314: GitHub is a read-only mirror (like Slack) — never compacted into the user's
          // own conversation_count, and its distilled notes are team-owned, so the card reads
          // mirror-table counts instead: repos connected + OPEN PRs updated in the last 14 days.
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
            <Metric value={(source.repo_count ?? 0).toLocaleString()} label="repos" />
            <Metric value={(source.pr_count ?? 0).toLocaleString()} label="open PRs" />
          </div>
        )
      ) : source.id === 'notion' ? (
        // Notion is team-scoped + store-only (never compacted), like GitHub/Slack — the headline
        // is mirrored pages, not the always-0 conversation_count. shared_pages_hint = connected
        // but nothing shared with the Terum connection yet, so prompt to share instead of "0 pages".
        isActive && source.shared_pages_hint ? (
          <p className={STAT_CLS}>Connected — share pages or databases with the Terum connection in Notion to start distilling.</p>
        ) : (
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
            <Metric value={(source.page_count ?? 0).toLocaleString()} label="pages" />
          </div>
        )
      ) : source.id === 'linear' ? (
        // Connected but the required "choose teams" step is unfinished → prompt instead of
        // showing 0/0 mirror metrics (nothing is ingested until the user chooses).
        source.teams_pending ? (
          <p className={STAT_CLS}>Connected — choose which teams Terum ingests to finish setup.</p>
        ) : (
          // Linear is a per-user read-only mirror (like GitHub) — never compacted into the user's
          // conversation_count, so the card reads mirror-table counts: issues + projects mirrored.
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
            <Metric value={(source.issue_count ?? 0).toLocaleString()} label="issues" />
            <Metric value={(source.project_count ?? 0).toLocaleString()} label="projects" />
          </div>
        )
      ) : source.id === 'granola' ? (
        // Granola is server-pull (like Slack/GitHub) — the headline is the live meeting count,
        // not conversation_count (one meeting fans out into several compacted topic rows).
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
          <Metric value={(source.meeting_count ?? 0).toLocaleString()} label="meetings" />
        </div>
      ) : source.id === 'claude-code' ? (
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
          <Metric value={source.conversation_count.toLocaleString()} label="sessions" />
        </div>
      ) : (
        <div className={`mt-3 flex flex-wrap gap-x-4 gap-y-1 ${isNotActive ? 'opacity-50' : ''}`}>
          <Metric value={isNotActive ? '—' : source.conversation_count.toLocaleString()} label="conversations" />
        </div>
      )}

      {/* No-silent-degradation: surface partial / stalled imports ON the card (the recovery
          action lives in the Manage drawer) so an incomplete import never reads as a clean ACTIVE. */}
      {isConversationSource && isActive && !capturing && source.backfill_partial && (
        <p className="mt-2 flex items-center gap-1.5 text-[12px] text-[#8a6a33] dark:text-[#c99b52]">
          <span className="size-1.5 rounded-full bg-[#c99b52]" /> Partial — {source.incomplete_count ?? 0} still need a re-fetch · Manage to retry
        </p>
      )}
      {/* tier-3 partial: Slack rate-limits DM history hard, so a run can finish partial.
          Surface the count (no-silent-degradation); the worker retries on the next sync. */}
      {source.id === 'slack_user' && isActive && source.backfill_partial && (
        <p className="mt-2 flex items-center gap-1.5 text-[12px] text-[#8a6a33] dark:text-[#c99b52]">
          <span className="size-1.5 rounded-full bg-[#c99b52]" /> Partial — {source.incomplete_count ?? 0} DMs still importing · retries automatically
        </p>
      )}
      {stale && captureProgress?.status === 'running' && (
        <p className="mt-2 flex items-center gap-1.5 text-[12px] text-[#8a6a33] dark:text-[#c99b52]">
          <span className="size-1.5 rounded-full bg-[#c99b52]" /> Import is taking longer than expected · Manage to retry
        </p>
      )}
      {/* Google connected but the calendar.readonly scope isn't granted yet (the connection
          predates the scope, so no meetings are flowing) — nudge a reconnect to add the calendar. */}
      {source.id === 'google' && isActive && source.calendar_sync_enabled === false && (
        <p className="mt-2 flex items-center gap-1.5 text-[12px] text-[#8a6a33] dark:text-[#c99b52]">
          <span className="size-1.5 rounded-full bg-[#c99b52]" /> Calendar not connected — reconnect Google (Manage) to add your meetings
        </p>
      )}

      {/* Slack team-briefing channel picker (the bot already joined these channels).
          Desktop-only: it's a management control, hidden on the read-only mobile card. */}
      {!isMobile && source.id === 'slack' && isActive && (source.channels?.length ?? 0) > 0 && (
        <div className="mt-3 flex items-center gap-2">
          <span className="whitespace-nowrap font-['DM_Mono'] text-[10px] tracking-[.1em] text-muted-foreground">TEAM BRIEFING →</span>
          <TeamChannelPicker
            channels={source.channels ?? []}
            selectedId={source.team_channel_id ?? null}
            disabled={isLoading}
            onSelect={channelId => { if (channelId !== source.team_channel_id) onSetTeamChannel(source.id, channelId); }}
          />
        </div>
      )}
      {source.id === 'slack' && isActive && (source.channels?.length ?? 0) === 0 && !source.team_channel_id && (
        <p className={STAT_CLS}>Add the Terum bot to a Slack channel and it'll post your team's daily briefing there.</p>
      )}

      {/* GitHub repo selection — the required setup CTA when pending, else an edit-selection link.
          Only Terum's selected repos are ingested (never all of them). Desktop-only management. */}
      {!isMobile && source.id === 'github' && isActive && (
        <div className="mt-3 flex items-center gap-2">
          <span className="whitespace-nowrap font-['DM_Mono'] text-[10px] tracking-[.1em] text-muted-foreground">
            {source.repos_pending ? 'SETUP →' : 'REPOSITORIES →'}
          </span>
          <button className={PILL_CLS} disabled={isLoading} onClick={() => onChooseRepos(source.id)}>
            {source.repos_pending ? 'Choose repositories' : `${source.selected_repo_count ?? 0} selected · Edit`}
          </button>
        </div>
      )}

      {/* Linear team selection — the required setup CTA when pending, else an edit-selection link.
          Only Terum's selected teams are ingested (never all of them). Desktop-only management. */}
      {!isMobile && source.id === 'linear' && isActive && (
        <div className="mt-3 flex items-center gap-2">
          <span className="whitespace-nowrap font-['DM_Mono'] text-[10px] tracking-[.1em] text-muted-foreground">
            {source.teams_pending ? 'SETUP →' : 'TEAMS →'}
          </span>
          <button className={PILL_CLS} disabled={isLoading} onClick={() => onChooseTeams(source.id)}>
            {source.teams_pending ? 'Choose teams' : `${source.selected_team_count ?? 0} selected · Edit`}
          </button>
        </div>
      )}

      {/* Privacy / account line. bug-373: don't claim "Capturing from <account>" when the
          source is signed out — the badge already says NOT SIGNED IN, so fall back to the
          neutral description instead of contradicting it. */}
      <p className="mt-2 flex-1 text-[12px] leading-4 text-muted-foreground/80">
        {isConversationSource && boundAccount && !signedOut ? `Capturing from ${boundAccount}` : (SOURCE_DESCRIPTIONS[source.id] ?? SOURCE_META[source.id]?.signal ?? '')}
      </p>

      {/* Footer actions (Activate / Reconnect / Manage) are the entry points to connecting,
          reconnecting, pausing, disconnecting, and deleting — all desktop-only. On mobile the
          card is view-only status; management happens on the desktop app. */}
      {!isMobile && (
        <div className="mt-3 flex justify-end border-t border-border pt-3">
          {isNotActive && !(source.id === 'claude-code' && source.conversation_count > 0) ? (
            // claude-code with leftover data but no live device stays NOT ACTIVE (decision C) but
            // still needs Manage to reach delete-data — so only a truly-empty claude-code shows Activate.
            <button className={PILL_PRIMARY_CLS} disabled={isLoading} onClick={() => onActivate(source.id)}>
              {isLoading ? 'Connecting…' : 'Activate →'}
            </button>
          ) : drifted ? (
            // Unit A: opens the two-verb account picker, not a rebind-only "reconnect".
            <button className={PILL_PRIMARY_CLS} disabled={isLoading} onClick={() => onReconnect(source.id)}>Choose account →</button>
          ) : (
            <button className={PILL_CLS} onClick={() => onManage(source.id)}>Manage</button>
          )}
        </div>
      )}
    </article>
  );
}

// d-connect: source-named consent drawer for chat sources, with the D13 "add the extension"
// step folded in (shown only while the extension isn't connected — it swaps to the consent
// step automatically once detection flips). Verbatim consent copy + the future-required guard
// are preserved from the old ConsentModal (still used by onboarding).
function ConnectDrawer({ sourceId, extStatus, onRetryExt, onConfirm, onClose }: {
  sourceId: string | null;
  extStatus: string;
  onRetryExt: () => void;
  onConfirm: (consentFuture: boolean, consentPast: boolean) => void;
  onClose: () => void;
}) {
  const [consentFuture, setConsentFuture] = useState(true);
  const [consentPast, setConsentPast] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (sourceId) { setConsentFuture(true); setConsentPast(true); setError(null); }
  }, [sourceId]);

  const open = !!sourceId;
  const name = sourceId ? sourceName(sourceId) : '';
  const needsExt = extStatus !== 'connected';

  function handleConfirm() {
    if (!consentFuture) {
      setError('Please allow Terum to record future conversations to connect this source');
      return;
    }
    setError(null);
    onConfirm(consentFuture, consentPast);
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={`Connect ${name}`}
      subtitle={needsExt ? 'Add the extension to capture this source' : 'Confirm what Terum may read'}
      icon={SHIELD_ICON}
    >
      {needsExt ? (
        <>
          <div className="dsub">
            Chat sources are captured in your browser, so Terum needs its Chrome extension to read your {name} conversations. We detect it automatically and bring you right back to finish connecting.
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            {extStatus === 'error' ? (
              <button className="btn btn-primary" onClick={onRetryExt}>Reconnect extension <span className="arr">→</span></button>
            ) : (
              <a className="btn btn-primary" href={CHROME_STORE_URL} target="_blank" rel="noopener noreferrer">Add to Chrome <span className="arr">→</span></a>
            )}
            <span className="sstat"><span className="dot amb" /> Not detected yet</span>
          </div>
        </>
      ) : (
        <>
          <div className="dgroup">
            <div className="dlabel">Permissions</div>
            <label className="di-check" style={{ cursor: 'pointer' }}>
              <input type="checkbox" checked={consentFuture} onChange={e => { setConsentFuture(e.target.checked); setError(null); }}
                style={{ width: 18, height: 18, accentColor: 'var(--acc)', flex: 'none', marginTop: 1 }} />
              <div><b style={{ color: 'var(--inkB)' }}>Allow Terum to record and store future conversations</b></div>
            </label>
            <label className="di-check" style={{ cursor: 'pointer' }}>
              <input type="checkbox" checked={consentPast} onChange={e => { setConsentPast(e.target.checked); setError(null); }}
                style={{ width: 18, height: 18, accentColor: 'var(--acc)', flex: 'none', marginTop: 1 }} />
              <div><b style={{ color: 'var(--inkB)' }}>Allow Terum to read the last 30 days of conversations</b></div>
            </label>
          </div>
          {error && (
            <div style={{ fontSize: 12, color: 'var(--rose)', lineHeight: 1.5, padding: '8px 12px', background: 'color-mix(in srgb, var(--rose) 8%, transparent)', borderRadius: 8 }}>
              {error}
            </div>
          )}
          <div className="dsub">Private / incognito conversations are never captured. Disconnect &amp; delete everything anytime.</div>
          <button className="btn btn-primary" style={{ justifyContent: 'center' }} onClick={handleConfirm}>Connect {name} <span className="arr">→</span></button>
        </>
      )}
    </Drawer>
  );
}

// d-connect (Claude Code variant): the CLI install guide. Claude Code connects on its own
// (no extension, no OAuth popup), so its "connect" surface is just the install walkthrough.
function SetupDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Connect Claude Code"
      subtitle="Capture your coding sessions automatically"
      icon={<span className="ltile" style={{ width: 36, height: 36 }}><SourceIcon id="claude-code" size={22} /></span>}
    >
      <ClaudeCodeInstall />
      <button className="btn btn-secondary" style={{ justifyContent: 'center' }} onClick={onClose}>Done</button>
    </Drawer>
  );
}

// d-devices: the Claude Code device-management drawer (the "right sidebar" for keys). Lists
// each connected machine (one active terum-capture api_key) with a per-device Revoke, an
// "Add a device" shortcut into the install guide, an optional View-sessions deep link, and a
// SEPARATE delete-data danger action. Revoke (credential) and delete (data) stay orthogonal
// (decision D) — revoking a machine never deletes captured sessions, and vice-versa.
function DevicesDrawer({ source, actionLoading, deleteLoading, onRevokeDevice, onDeleteData, onAddDevice, onView, onClose }: {
  source: Source | null;
  actionLoading: string | null;   // the keyId currently being revoked
  deleteLoading: boolean;
  onRevokeDevice: (keyId: string) => void;
  onDeleteData: () => void;
  onAddDevice: () => void;
  onView: (id: string) => void;
  onClose: () => void;
}) {
  const devices = source?.devices ?? [];
  const hasData = (source?.conversation_count ?? 0) > 0;
  const view = KNOWLEDGE_VIEW['claude-code'];
  return (
    <Drawer
      open={!!source}
      onClose={onClose}
      title="Claude Code devices"
      subtitle={devices.length > 0 ? `${devices.length} device${devices.length > 1 ? 's' : ''} connected` : 'No devices connected'}
      icon={<span className="ltile" style={{ width: 36, height: 36 }}><SourceIcon id="claude-code" size={22} /></span>}
    >
      {source && (
        <>
          <div className="dgroup">
            <div className="dlabel">Connected devices</div>
            {devices.length === 0 ? (
              <div className="dsub">
                No devices connected. Run <span style={{ fontFamily: "'DM Mono', monospace", color: 'var(--inkB)' }}>terum-capture setup</span> on a machine to start capturing your Claude Code sessions.
              </div>
            ) : (
              devices.map(d => (
                <div className="drow" key={d.id}>
                  <span className="dk">
                    {d.name || 'Unknown device'}
                    <br />
                    <span className="dsub">
                      {d.last_used_at ? `last used ${relativeTime(d.last_used_at)}` : 'never used yet'}
                      {d.key_prefix ? ` · ${d.key_prefix}…` : ''}
                    </span>
                  </span>
                  <span className="dv">
                    <button
                      className="btn btn-secondary sm"
                      disabled={actionLoading === d.id}
                      style={{ ...ROSE_BTN, opacity: actionLoading === d.id ? 0.6 : 1 }}
                      onClick={() => onRevokeDevice(d.id)}
                    >
                      {actionLoading === d.id ? 'Revoking…' : 'Revoke'}
                    </button>
                  </span>
                </div>
              ))
            )}
            <button className="btn btn-secondary sm" style={{ alignSelf: 'flex-start' }} onClick={onAddDevice}>
              Add a device <span className="arr">→</span>
            </button>
          </div>

          {view && hasData && (
            <div className="dgroup">
              <button className="btn btn-secondary sm" style={{ alignSelf: 'flex-start' }} onClick={() => onView('claude-code')}>
                {view.label} <span className="arr">→</span>
              </button>
            </div>
          )}

          <div className="dgroup">
            <div className="dlabel">How it works</div>
            <div className="dsub">
              Each machine you run <span style={{ fontFamily: "'DM Mono', monospace", color: 'var(--inkB)' }}>terum-capture setup</span> on gets its own key. Revoking one stops that machine from syncing — it doesn't touch the data it already captured.
            </div>
          </div>

          {/* Delete data — SEPARATE from revoke (decision D). Available whenever there are
              devices OR leftover captured data (so you can clean up after revoking your last machine). */}
          {(devices.length > 0 || hasData) && (
            <div className="danger-zone">
              <div className="drow" style={{ border: 'none', padding: 0 }}>
                <span className="dk" style={{ color: 'var(--rose)' }}>Delete Claude Code data</span>
                <span className="dv">
                  <button className="btn btn-secondary sm" disabled={deleteLoading} style={{ ...ROSE_BTN, opacity: deleteLoading ? 0.6 : 1 }} onClick={onDeleteData}>
                    {deleteLoading ? 'Deleting…' : 'Delete data'}
                  </button>
                </span>
              </div>
              <div className="dsub" style={{ marginTop: 6 }}>
                Removes all captured Claude Code sessions and their distilled notes. Your devices stay connected unless you revoke them above.
              </div>
            </div>
          )}
        </>
      )}
    </Drawer>
  );
}

// Typed-confirm dialog for deleting Claude Code data (decision D preview). Destructive +
// irreversible, so the confirm button arms only once "delete" is typed — mirrors the
// account-level "delete my data" pattern. `.di` is drawer-scoped, so the input is inline-styled.
function ClaudeCodeDeleteDataDialog({ open, loading, onConfirm, onCancel }: {
  open: boolean;
  loading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const [text, setText] = useState('');
  useEffect(() => { if (open) setText(''); }, [open]);
  if (!open) return null;
  const armed = text.trim().toLowerCase() === 'delete';
  return (
    <div onClick={loading ? undefined : onCancel} style={DIALOG_BACKDROP}>
      <div onClick={e => e.stopPropagation()} style={{ ...DIALOG_CARD, borderColor: 'color-mix(in srgb, var(--rose) 30%, transparent)' }}>
        <button onClick={onCancel} disabled={loading} aria-label="Close" style={{
          position: 'absolute', top: 12, right: 14, background: 'none', border: 'none',
          color: 'var(--inkS)', fontSize: 22, lineHeight: 1, cursor: loading ? 'default' : 'pointer', padding: 0,
        }}>×</button>
        <div style={{ fontSize: 17, fontWeight: 600, color: 'var(--rose)', marginBottom: 12 }}>Delete Claude Code data</div>
        <div style={{ fontSize: 13, color: 'var(--inkS)', lineHeight: 1.6, marginBottom: 16 }}>
          Permanently deletes every captured Claude Code session and its distilled notes. Your connected devices stay connected (revoke them separately). This can't be undone.
        </div>
        <div style={{ fontSize: 12, color: 'var(--inkS)', marginBottom: 6 }}>
          Type <b style={{ color: 'var(--inkB)' }}>delete</b> to confirm:
        </div>
        <input
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder="delete"
          disabled={loading}
          autoFocus
          style={{
            width: '100%', boxSizing: 'border-box', marginBottom: 18, padding: '9px 12px',
            background: 'var(--panel)', border: '1px solid var(--hair2)', borderRadius: 8,
            color: 'var(--inkB)', fontSize: 13, fontFamily: "'DM Sans', sans-serif",
          }}
        />
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-secondary" disabled={loading} style={{ flex: 1, justifyContent: 'center' }} onClick={onCancel}>Cancel</button>
          <button className="btn btn-secondary" disabled={loading || !armed} style={{ flex: 1, justifyContent: 'center', ...ROSE_BTN, opacity: (loading || !armed) ? 0.5 : 1 }} onClick={onConfirm}>
            {loading ? 'Deleting…' : 'Delete data'}
          </button>
        </div>
      </div>
    </div>
  );
}

// d-connect (Slack tier-3 / per-user DMs): the consent step for connecting your OWN Slack
// DMs + private channels, DISTINCT from the admin bot install. Carries the DEC-1b connector
// disclosure (the other party never consented to ingestion, so widening the audience to the
// DM participants post-spine is an obligation we disclose up front). The OAuth popup opens on
// the confirm click (a real user gesture), so it isn't blocked. Exported for render tests.
export function SlackDmConnectDrawer({ open, onConnect, onClose }: {
  open: boolean;
  onConnect: () => void;
  onClose: () => void;
}) {
  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Connect your Slack DMs"
      subtitle="Your own DMs & private channels — distinct from the workspace bot"
      icon={<span className="ltile" style={{ width: 36, height: 36 }}><SourceIcon id="slack_user" size={22} /></span>}
    >
      {open && (
        <>
          <div className="dgroup">
            <div className="dlabel">What Terum reads</div>
            <div className="dsub">
              Terum reads <b style={{ color: 'var(--inkB)' }}>your own</b> Slack DMs, group DMs, and private channels and distills the decisions in them into your <b style={{ color: 'var(--inkB)' }}>private</b> knowledge — never the team's shared graph, never your coworkers' own messages.
            </div>
          </div>
          {/* DEC-1b connector disclosure — shown at connect (reconciliation #2, 2026-06-26). */}
          <div style={{ fontSize: 12.5, color: 'var(--amb)', lineHeight: 1.55, padding: '11px 13px', background: 'color-mix(in srgb, var(--amb) 8%, transparent)', border: '1px solid color-mix(in srgb, var(--amb) 28%, transparent)', borderRadius: 9 }}>
            Heads up: decisions in your DMs and private channels may be shared with the teammates who were in them.
          </div>
          <div className="dsub">Your Slack token is encrypted at rest. Disconnect &amp; delete everything anytime.</div>
          <button className="btn btn-primary" style={{ justifyContent: 'center' }} onClick={onConnect}>
            Connect your Slack DMs <span className="arr">→</span>
          </button>
        </>
      )}
    </Drawer>
  );
}

// d-connect (Granola): Granola authenticates with a pasted API key (server-side pull, no OAuth
// popup, no extension), so its connect surface is a single key field. The POST validates the key
// server-side before persisting; a bad key comes back as a 400 the drawer shows inline. Exported
// for render tests.
export function GranolaConnectDrawer({ open, onConnect, onClose }: {
  open: boolean;
  onConnect: (apiKey: string) => Promise<{ ok: boolean; error?: string }>;
  onClose: () => void;
}) {
  const [apiKey, setApiKey] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { if (open) { setApiKey(''); setSubmitting(false); setError(null); } }, [open]);

  async function handleConnect() {
    const key = apiKey.trim();
    if (!key) { setError('Paste your Granola API key to connect.'); return; }
    setSubmitting(true);
    setError(null);
    const res = await onConnect(key);
    // On success the parent closes the drawer (and refetches the card); only reset local state
    // on failure so the "Connecting…" button never flickers back before the drawer unmounts.
    if (!res.ok) { setError(res.error ?? 'Failed to connect Granola'); setSubmitting(false); }
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Connect Granola"
      subtitle="Paste your Granola API key"
      icon={<span className="ltile" style={{ width: 36, height: 36 }}><SourceIcon id="granola" size={22} /></span>}
    >
      {open && (
        <>
          <div className="dgroup">
            <div className="dlabel">What Terum reads</div>
            <div className="dsub">
              Terum reads your Granola meeting notes and transcripts (read-only) and distills each meeting — its topics, decisions, and who participated — into your knowledge graph. It never edits or deletes anything in Granola.
            </div>
          </div>
          <div className="dgroup">
            <div className="dlabel">API key</div>
            <input
              type="password"
              value={apiKey}
              onChange={e => { setApiKey(e.target.value); setError(null); }}
              placeholder="Paste your Granola API key"
              disabled={submitting}
              autoFocus
              onKeyDown={e => { if (e.key === 'Enter' && !submitting) handleConnect(); }}
              style={{
                width: '100%', boxSizing: 'border-box', padding: '9px 12px',
                background: 'var(--panel)', border: '1px solid var(--hair2)', borderRadius: 8,
                color: 'var(--inkB)', fontSize: 13, fontFamily: "'DM Sans', sans-serif",
              }}
            />
          </div>
          {error && (
            <div style={{ fontSize: 12, color: 'var(--rose)', lineHeight: 1.5, padding: '8px 12px', background: 'color-mix(in srgb, var(--rose) 8%, transparent)', borderRadius: 8 }}>
              {error}
            </div>
          )}
          <div className="dsub">Your Granola API key is encrypted at rest. Disconnect &amp; delete everything anytime.</div>
          <button className="btn btn-primary" style={{ justifyContent: 'center' }} disabled={submitting} onClick={handleConnect}>
            {submitting ? 'Connecting…' : <>Connect Granola <span className="arr">→</span></>}
          </button>
        </>
      )}
    </Drawer>
  );
}

/** The observed (newly signed-in) account's display name. Claude has NO account email — its
 *  identity report carries only an org label — so the label is a first-class fallback, not a
 *  nicety. Generic last resort so the copy never renders "signed in as null". */
export function describeObservedAccount(account: SourceAccount | null | undefined): string {
  return account?.observedEmail || account?.observedLabel || 'another account';
}

// d-picker: the chat-source ACCOUNT PICKER (unit A, 2026-07-29). Supersedes the rebind-only
// "reconnect" drawer and its D13 ruling: a second signed-in account is a normal choice, not an
// error, so both server verbs are offered — rebind (capture the new account from now on) and
// ignore (keep the bound account; capture stays paused until it is signed back in, and resumes
// by itself when the extension's next identity report matches). Capture-paused is stated in both
// branches because it remains true either way — the privacy guard is unchanged, only the framing.
export function AccountPickerDrawer({ source, loading, onCapture, onKeep, onClose }: {
  source: Source | null;
  loading: boolean;
  onCapture: (id: string, email?: string) => void;
  onKeep: (id: string) => void;
  onClose: () => void;
}) {
  const account = source?.source_account ?? null;
  const observed = describeObservedAccount(account);
  const bound = account?.email || account?.label || 'the account you connected';
  return (
    <Drawer
      open={!!source}
      onClose={onClose}
      title={source ? `Which ${source.name} account?` : ''}
      subtitle="Signed into a different account · capture paused"
      icon={source ? <span className="ltile" style={{ width: 36, height: 36 }}><SourceIcon id={source.id} size={22} /></span> : undefined}
    >
      {source && (
        <>
          <div className="drift">
            {source.name} is signed in as <b>{observed}</b>, and Terum is set up to capture <b>{bound}</b>. Capture is paused until you choose.
          </div>
          <div className="dgroup">
            <div className="dlabel">Choose</div>
            <div className="drow">
              <span className="dk">Capture <b style={{ color: 'var(--inkB)' }}>{observed}</b><br /><span className="dsub">From now on. Notes already captured stay as they are.</span></span>
              <span className="dv">
                <button className="btn btn-primary sm" disabled={loading} onClick={() => onCapture(source.id, account?.observedEmail ?? undefined)}>
                  {loading ? 'Switching…' : 'Capture this one'}
                </button>
              </span>
            </div>
            <div className="drow">
              <span className="dk">Keep <b>{bound}</b><br /><span className="dsub">Stays paused while the other account is signed in, and resumes on its own when you sign back in.</span></span>
              <span className="dv">
                <button className="btn sm" disabled={loading} onClick={() => onKeep(source.id)}>
                  {loading ? 'Saving…' : 'Keep it paused'}
                </button>
              </span>
            </div>
          </div>
        </>
      )}
    </Drawer>
  );
}

// d-src: the per-source manage drawer. Relocates the old inline footer / kebab actions —
// backfill progress, pause/resume, resync, stalled-import recovery, cancel-import, and the
// disconnect danger-zone — into one right-side panel.
function ManageDrawer({
  source, captureProgress, stale, capturing, actionLoading, removeLoading,
  onRequestPause, onResume, onReconnectPaused, onResync, onChooseRepos, onChooseTeams, onBackfill, onSkip, onCancelImport, onRemove, onView, onClose,
}: {
  source: Source | null;
  captureProgress: CaptureProgress | null;
  stale: boolean;
  capturing: boolean;
  actionLoading: string | null;
  removeLoading: boolean;
  onRequestPause: (id: string) => void;
  onResume: (id: string) => void;
  onReconnectPaused: (id: string) => void;
  onResync: (id: string) => void;
  onChooseRepos: (id: string) => void;
  onChooseTeams: (id: string) => void;
  onBackfill: (id: string) => void;
  onSkip: (id: string) => void;
  onCancelImport: (id: string) => void;
  onRemove: (id: string) => void;
  onView: (id: string) => void;
  onClose: () => void;
}) {
  const isChat = source ? CONVERSATION_SOURCES.includes(source.id) : false;
  // OAuth/server-pull sources pause by revoking their token (app/api/surfaces/sources/route.ts
  // pauseOAuthSource) — there's no flag to flip server-side, so resuming is a fresh reconnect,
  // not onResume. CONVERSATION_SOURCES.includes already covers isChat; this is the other half.
  // granola is server-pull + disconnectable (so it's in OAUTH_SOURCES for the danger-zone +
  // synced-recency), but the MVP has no granola pause endpoint (it's absent from the route's
  // OAUTH_PAUSE_CONFIG), so it shows Disconnect but never Pause. Every other OAUTH_SOURCE pauses.
  const isOAuthPausable = source ? (OAUTH_SOURCES.includes(source.id) && source.id !== 'granola') : false;
  const isLoading = source ? actionLoading === source.id : false;
  const account = source?.source_account ?? null;
  const isPaused = source?.status === 'paused';
  const isActive = source?.status === 'active';
  const pct = captureProgress && captureProgress.totalConversations > 0
    ? Math.min(Math.max(Math.round((captureProgress.conversationsProcessed / captureProgress.totalConversations) * 100), 1), 99)
    : null;
  const recency = source && OAUTH_SOURCES.includes(source.id)
    ? (source.last_synced_at ? relativeTime(source.last_synced_at) : 'awaiting first sync')
    : (source?.last_activity ? relativeTime(source.last_activity) : 'no activity yet');

  return (
    <Drawer
      open={!!source}
      onClose={onClose}
      title={source?.name ?? ''}
      subtitle={account?.email ? `Connected · ${account.email}` : 'Connected'}
      icon={source ? <span className="ltile" style={{ width: 36, height: 36 }}><SourceIcon id={source.id} size={22} /></span> : undefined}
    >
      {source && (
        <>
          {/* View this source's distilled data in the Knowledge list (/map · List), filtered
              to its site. Only for sources whose data actually lands there (KNOWLEDGE_VIEW). */}
          {KNOWLEDGE_VIEW[source.id] && (
            <div className="dgroup">
              <button
                className="btn btn-secondary sm"
                style={{ alignSelf: 'flex-start' }}
                onClick={() => onView(source.id)}
              >
                {KNOWLEDGE_VIEW[source.id].label} <span className="arr">→</span>
              </button>
            </div>
          )}

          {/* Backfill progress (while importing) + Cancel. */}
          {capturing && (
            <div className="dgroup">
              <div className="dlabel">Backfill</div>
              {pct !== null && <div className="bar"><i style={{ width: `${pct}%` }} /></div>}
              <div className="dsub">
                {pct !== null
                  ? `Importing history — ${pct}%  (${captureProgress!.conversationsProcessed.toLocaleString()} / ${captureProgress!.totalConversations.toLocaleString()} conversations)`
                  : 'Importing history…'}
              </div>
              <button className="btn btn-secondary sm" disabled={isLoading} style={{ ...ROSE_BTN, alignSelf: 'flex-start', opacity: isLoading ? 0.6 : 1 }} onClick={() => onCancelImport(source.id)}>
                {isLoading ? 'Cancelling…' : 'Cancel import'}
              </button>
            </div>
          )}

          {/* bug-203: stalled-import recovery (a live run with no fresh updates). */}
          {stale && captureProgress?.status === 'running' && (
            <div className="dgroup">
              <div className="dlabel" style={{ color: 'var(--rose)' }}>Import is taking longer than expected</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-secondary sm" disabled={isLoading} onClick={() => onBackfill(source.id)}>Retry</button>
                <button className="btn btn-secondary sm" disabled={isLoading} style={{ color: 'var(--inkS)' }} onClick={() => onSkip(source.id)}>Skip import</button>
              </div>
            </div>
          )}

          {/* bug-207: a finished run with conversations still needing a detail re-fetch ends PARTIAL. */}
          {isChat && isActive && !capturing && source.backfill_partial && (
            <div className="dgroup">
              <div className="dlabel" style={{ color: 'var(--amb)' }}>Partial import</div>
              <button className="btn btn-secondary sm" disabled={isLoading} style={{ alignSelf: 'flex-start', color: 'var(--amb)', borderColor: 'color-mix(in srgb, var(--amb) 40%, transparent)', opacity: isLoading ? 0.6 : 1 }} onClick={() => onBackfill(source.id)}>
                {isLoading ? 'Loading…' : `Retry ${source.incomplete_count ?? 0} unfinished`}
              </button>
            </div>
          )}

          {/* tier-3 backfill / partial — informational only (the DM backfill is a server worker,
              not the extension, so there is no SPA-side retry; it resumes on the next sync). */}
          {source.id === 'slack_user' && isActive && !source.backfill_complete && (
            <div className="dgroup">
              <div className="dlabel">Backfill</div>
              <div className="dsub">Importing your Slack DMs…</div>
            </div>
          )}
          {source.id === 'slack_user' && isActive && source.backfill_partial && (
            <div className="dgroup">
              <div className="dlabel" style={{ color: 'var(--amb)' }}>Partial import</div>
              <div className="dsub">{source.incomplete_count ?? 0} DMs still need a re-fetch — Terum retries automatically on the next sync.</div>
            </div>
          )}

          {/* Capture controls. */}
          <div className="dgroup">
            <div className="dlabel">Capture</div>
            {source.id === 'google' && (
              <div className="drow">
                <span className="dk">Resync now</span>
                <span className="dv"><button className="btn btn-secondary sm" disabled={isLoading} onClick={() => onResync(source.id)}>{isLoading ? 'Syncing…' : 'Resync'}</button></span>
              </div>
            )}
            {source.id === 'github' && (
              <div className="drow">
                <span className="dk">Repositories</span>
                <span className="dv">
                  {(source.selected_repo_count ?? 0)} ingested
                  <button className="btn btn-secondary sm" style={{ marginLeft: 10 }} disabled={isLoading} onClick={() => onChooseRepos(source.id)}>Choose</button>
                </span>
              </div>
            )}
            {source.id === 'linear' && (
              <div className="drow">
                <span className="dk">Teams</span>
                <span className="dv">
                  {(source.selected_team_count ?? 0)} ingested
                  <button className="btn btn-secondary sm" style={{ marginLeft: 10 }} disabled={isLoading} onClick={() => onChooseTeams(source.id)}>Choose</button>
                </span>
              </div>
            )}
            <div className="drow">
              <span className="dk">{OAUTH_SOURCES.includes(source.id) ? 'Last synced' : 'Last captured'}</span>
              <span className="dv mono">{recency}</span>
            </div>
            {isChat && account?.email && (
              <div className="drow">
                <span className="dk">Account</span>
                <span className="dv"><span className="dot grn" />{account.email}</span>
              </div>
            )}
            {/* Explicit Pause/Resume — not a silent toggle, so resuming an OAuth source's real
                cost (re-authorizing from scratch) is never one accidental click away. Pausing
                always confirms first (PauseConfirmDialog); resuming a chat source is a single
                reversible click, resuming an OAuth source reopens its connect flow. */}
            {(isChat || isOAuthPausable) && (
              <div className="drow">
                <span className="dk">{isPaused ? 'Paused' : 'Capture'}</span>
                <span className="dv">
                  {isPaused ? (
                    <button
                      className="btn btn-primary sm"
                      disabled={capturing || isLoading}
                      onClick={() => (isOAuthPausable ? onReconnectPaused(source.id) : onResume(source.id))}
                    >
                      {isLoading
                        ? (isOAuthPausable ? 'Opening…' : 'Resuming…')
                        : (isOAuthPausable ? 'Reconnect to resume' : 'Resume')}
                    </button>
                  ) : (
                    <button
                      className="btn btn-secondary sm"
                      disabled={capturing || isLoading}
                      style={{ ...AMBER_BTN, opacity: (capturing || isLoading) ? 0.5 : 1 }}
                      // Do NOT close the drawer here. This only OPENS a confirmation — closing on
                      // the same click meant Cancel dropped the user on the bare Sources grid
                      // instead of back in the drawer they were reading. The confirm dialog closes
                      // the drawer itself once the action actually goes through.
                      onClick={() => onRequestPause(source.id)}
                    >
                      Pause
                    </button>
                  )}
                </span>
              </div>
            )}
          </div>

          <div className="dgroup">
            <div className="dlabel">Privacy</div>
            <div className="dsub">
              {SOURCE_DESCRIPTIONS[source.id] ?? ''} Private / incognito conversations are never captured. You can mark any single conversation private later.
            </div>
          </div>

          {/* Disconnect danger-zone — only for sources that can be revoked (chat + OAuth). */}
          {(isChat || OAUTH_SOURCES.includes(source.id)) && (
            <div className="danger-zone">
              <div className="drow" style={{ border: 'none', padding: 0 }}>
                <span className="dk" style={{ color: 'var(--rose)' }}>Disconnect &amp; delete data</span>
                <span className="dv">
                  {/* Same as Pause above: this opens the confirm, it does not perform the
                      disconnect, so the drawer must stay behind it for Cancel to return to. */}
                  <button className="btn btn-secondary sm" disabled={removeLoading} style={ROSE_BTN} onClick={() => onRemove(source.id)}>Disconnect</button>
                </span>
              </div>
            </div>
          )}
        </>
      )}
    </Drawer>
  );
}

interface PickerRepo {
  github_id: number;
  full_name: string;
  name: string;
  owner: string;
  private: boolean;
  pushed_at: string | null;
  selected: boolean;
}

// Keep in lockstep with SELECTED_REPO_LIMIT in the MVP's lib/ingest/github/selection.ts — the
// backend rejects a longer list, so the picker caps here to give an inline message instead of a 400.
const MAX_SELECTED_REPOS = 20;

// The GitHub repo-selection picker — the REQUIRED setup step after connecting GitHub, and the
// edit-later entry point from Manage. Terum only mirrors the repos chosen here; nothing is ingested
// until a selection is saved. Deselecting a repo purges its mirrored data server-side.
function GithubRepoPicker({ open, onClose, onSave, saving }: {
  open: boolean;
  onClose: () => void;
  onSave: (repoIds: number[]) => void;
  saving: boolean;
}) {
  const [repos, setRepos] = useState<PickerRepo[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reauth, setReauth] = useState(false);
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const [search, setSearch] = useState('');

  // Fetch the user's accessible repos each time the drawer opens (fresh state, no stale list).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true); setError(null); setReauth(false); setRepos(null); setSearch('');
    (async () => {
      try {
        const res = await apiFetch<{ repos: PickerRepo[] }>('/api/surfaces/sources/github-repos');
        if (cancelled) return;
        const list = res.repos ?? [];
        setRepos(list);
        setChecked(new Set(list.filter(r => r.selected).map(r => r.github_id)));
      } catch (err) {
        if (cancelled) return;
        // A 409 means the stored GitHub token is gone/invalid — prompt a reconnect, not an empty list.
        // Branch on the structured status (api.ts ApiError), never string-match the message.
        if (err instanceof ApiError && err.status === 409) setReauth(true);
        else setError(err instanceof Error ? err.message : 'Failed to load repositories');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const toggle = (id: number) => {
    setChecked(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < MAX_SELECTED_REPOS) next.add(id);
      return next;
    });
  };

  const q = search.trim().toLowerCase();
  const filtered = (repos ?? []).filter(r => r.full_name.toLowerCase().includes(q));
  const atCap = checked.size >= MAX_SELECTED_REPOS;
  const hairline = 'color-mix(in srgb, var(--ink) 14%, transparent)';

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Choose repositories"
      subtitle="Terum only ingests the repositories you select"
      icon={<span className="ltile" style={{ width: 36, height: 36 }}><SourceIcon id="github" size={22} /></span>}
    >
      {loading && <div className="dsub">Loading your repositories…</div>}

      {reauth && (
        <div className="dgroup">
          <div className="dlabel" style={{ color: 'var(--rose)' }}>GitHub needs reconnecting</div>
          <div className="dsub">Your GitHub connection expired. Close this, reconnect GitHub from the card, then choose repositories.</div>
        </div>
      )}

      {error && !reauth && (
        <div className="dgroup"><div className="dsub" style={{ color: 'var(--rose)' }}>{error}</div></div>
      )}

      {repos && !loading && !reauth && (
        <>
          <div className="dsub">
            Pick up to {MAX_SELECTED_REPOS} repositories. Deselecting one removes its mirrored data. Nothing is ingested until you save.
          </div>
          <input
            placeholder="Search repositories…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', margin: '10px 0', background: 'transparent', border: `1px solid ${hairline}`, borderRadius: 6, color: 'var(--ink)', fontSize: 13 }}
          />
          {filtered.length === 0 ? (
            <div className="dsub">{repos.length === 0 ? 'No repositories found for your GitHub account.' : 'No repositories match your search.'}</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 360, overflowY: 'auto', margin: '2px 0' }}>
              {filtered.map(r => {
                const isChecked = checked.has(r.github_id);
                const disabled = !isChecked && atCap;
                return (
                  <label key={r.github_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 6px', borderRadius: 6, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1 }}>
                    <input type="checkbox" checked={isChecked} disabled={disabled || saving} onChange={() => toggle(r.github_id)} />
                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13, color: 'var(--ink)' }}>{r.full_name}</span>
                    {r.private && (
                      <span style={{ fontSize: 10, letterSpacing: '0.06em', color: 'var(--amb)', border: '1px solid color-mix(in srgb, var(--amb) 40%, transparent)', borderRadius: 4, padding: '1px 5px' }}>PRIVATE</span>
                    )}
                    {r.pushed_at && <span style={{ fontSize: 11, color: 'var(--faint)', whiteSpace: 'nowrap' }}>{relativeTime(r.pushed_at)}</span>}
                  </label>
                );
              })}
            </div>
          )}
          <div className="dgroup" style={{ marginTop: 10 }}>
            <div className="dsub">{checked.size} of {MAX_SELECTED_REPOS} selected{atCap ? ' · limit reached' : ''}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary sm" disabled={saving} onClick={() => onSave([...checked])}>
                {saving ? 'Saving…' : checked.size === 0 ? 'Save (ingest nothing)' : `Ingest ${checked.size} ${checked.size === 1 ? 'repo' : 'repos'}`}
              </button>
              <button className="btn btn-secondary sm" disabled={saving} onClick={onClose}>Cancel</button>
            </div>
          </div>
        </>
      )}
    </Drawer>
  );
}

interface PickerTeam { linear_team_id: string; key: string | null; name: string | null; selected: boolean }

// The Linear team-selection picker — the REQUIRED setup step after connecting Linear, and the
// edit-later entry point from Manage. Terum only mirrors the teams chosen here; nothing is ingested
// until a selection is saved. Deselecting a team purges its mirrored data server-side. No count cap.
function LinearTeamPicker({ open, onClose, onSave, saving }: {
  open: boolean; onClose: () => void; onSave: (teamIds: string[]) => void; saving: boolean;
}) {
  const [teams, setTeams] = useState<PickerTeam[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reauth, setReauth] = useState(false);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true); setError(null); setReauth(false); setTeams(null); setSearch('');
    (async () => {
      try {
        const res = await apiFetch<{ teams: PickerTeam[] }>('/api/surfaces/sources/linear-teams');
        if (cancelled) return;
        const list = res.teams ?? [];
        setTeams(list);
        setChecked(new Set(list.filter(t => t.selected).map(t => t.linear_team_id)));
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 409) setReauth(true);
        else setError(err instanceof Error ? err.message : 'Failed to load teams');
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const toggle = (id: string) => setChecked(prev => {
    const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next;
  });
  const q = search.trim().toLowerCase();
  const filtered = (teams ?? []).filter(t => `${t.key ?? ''} ${t.name ?? ''}`.toLowerCase().includes(q));
  const hairline = 'color-mix(in srgb, var(--ink) 14%, transparent)';

  return (
    <Drawer open={open} onClose={onClose} title="Choose teams" subtitle="Terum only ingests the teams you select"
      icon={<span className="ltile" style={{ width: 36, height: 36 }}><SourceIcon id="linear" size={22} /></span>}>
      {loading && <div className="dsub">Loading your teams…</div>}
      {reauth && (
        <div className="dgroup">
          <div className="dlabel" style={{ color: 'var(--rose)' }}>Linear needs reconnecting</div>
          <div className="dsub">Your Linear connection expired. Close this, reconnect Linear from the card, then choose teams.</div>
        </div>
      )}
      {error && !reauth && (<div className="dgroup"><div className="dsub" style={{ color: 'var(--rose)' }}>{error}</div></div>)}
      {teams && !loading && !reauth && (
        <>
          <div className="dsub">Pick the teams whose issues Terum mirrors. Deselecting one removes its mirrored data. Nothing is ingested until you save.</div>
          <input placeholder="Search teams…" value={search} onChange={e => setSearch(e.target.value)}
            style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', margin: '10px 0', background: 'transparent', border: `1px solid ${hairline}`, borderRadius: 6, color: 'var(--ink)', fontSize: 13 }} />
          {filtered.length === 0 ? (
            <div className="dsub">{teams.length === 0 ? 'No teams found in your Linear workspace.' : 'No teams match your search.'}</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 360, overflowY: 'auto', margin: '2px 0' }}>
              {filtered.map(t => (
                <label key={t.linear_team_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 6px', borderRadius: 6, cursor: 'pointer' }}>
                  <input type="checkbox" checked={checked.has(t.linear_team_id)} disabled={saving} onChange={() => toggle(t.linear_team_id)} />
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13, color: 'var(--ink)' }}>
                    {t.key ? `${t.key} · ` : ''}{t.name ?? t.linear_team_id}
                  </span>
                </label>
              ))}
            </div>
          )}
          <div className="dgroup" style={{ marginTop: 10 }}>
            <div className="dsub">{checked.size} selected</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary sm" disabled={saving} onClick={() => onSave([...checked])}>
                {saving ? 'Saving…' : checked.size === 0 ? 'Save (ingest nothing)' : `Ingest ${checked.size} ${checked.size === 1 ? 'team' : 'teams'}`}
              </button>
              <button className="btn btn-secondary sm" disabled={saving} onClick={onClose}>Cancel</button>
            </div>
          </div>
        </>
      )}
    </Drawer>
  );
}

export function SourcesSurface({ data, onNav, userEmail }: SourcesSurfaceProps) {
  // bug-207: gate chat-source connect on the *handshaken* extension (single app-wide
  // bridge, via context). 'connected' = a Terum token is stored — the only state in which
  // a backfill can actually run. retryExtension re-runs the handshake (the RECONNECT CTA).
  const { status: extStatus, retry: retryExtension } = useExtensionStatus();
  // Mobile: every connect/re-import path needs the desktop extension + an OAuth popup, so
  // on a phone we intercept them and open a "do this on your computer" sheet instead. The
  // read-only source status below is the mobile value; nothing that fires the extension
  // handshake or an OAuth popup may run here.
  const isMobile = useIsMobile();
  const [desktopSheet, setDesktopSheet] = useState<{ open: boolean; source?: string }>({ open: false });
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [consentPending, setConsentPending] = useState<string | null>(null);
  const [setupOpen, setSetupOpen] = useState(false);
  // Slack tier-3 (per-user DMs) consent drawer — distinct from the chat consent + the bot install.
  const [slackDmConsentOpen, setSlackDmConsentOpen] = useState(false);
  // Granola connect drawer (API-key paste) — distinct from OAuth popups + the chat consent.
  const [granolaConnectOpen, setGranolaConnectOpen] = useState(false);
  const [managePending, setManagePending] = useState<string | null>(null);
  const [reconnectPending, setReconnectPending] = useState<string | null>(null);
  // Claude Code device-management drawer ('claude-code' when open) + its delete-data dialog.
  const [devicesPending, setDevicesPending] = useState<string | null>(null);
  const [deleteDataOpen, setDeleteDataOpen] = useState(false);
  const [deleteDataLoading, setDeleteDataLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // The error banner lives at the top of the surface; a connect/pause/remove action fired while
  // scrolled down the sources list would leave the resulting error off-screen. Scroll it into view
  // whenever a message appears so a failure is never silently missed (setErrorMessage(null) is
  // guarded out, so clearing an error never yanks the page).
  const errorBannerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!errorMessage) return;
    // Defer one frame before scrolling. The OAuth-connect errors (e.g. the Slack "already
    // connected to another team" block) are set as the OAuth popup closes, and the browser
    // returns focus to the opener in that same instant — a scrollIntoView issued synchronously
    // in that window is dropped. rAF runs it after the commit + focus return, so the scroll is
    // reliable for the whole class of source-page errors, not just the in-page button actions.
    const raf = requestAnimationFrame(() =>
      errorBannerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    );
    return () => cancelAnimationFrame(raf);
  }, [errorMessage]);
  // Per-source Remove confirmation (which source's confirm is open) + the global "Delete All".
  const [removePending, setRemovePending] = useState<string | null>(null);
  const [removeLoading, setRemoveLoading] = useState(false);
  // Per-source Pause confirmation (which source's confirm is open).
  const [pausePending, setPausePending] = useState<string | null>(null);
  const [deleteAllOpen, setDeleteAllOpen] = useState(false);
  const [deleteAllLoading, setDeleteAllLoading] = useState(false);
  const [captureProgress, setCaptureProgress] = useState<Record<string, CaptureProgress>>({});
  const [staleCaptures, setStaleCaptures] = useState<Set<string>>(new Set());
  const lastUpdateRef = useRef<Record<string, number>>({});
  // Clear the optimistic per-source capture UI state (progress bar + stale marker) in one
  // call — shared by cancelImport, skipImport, and the backfill failure path.
  const clearCaptureState = useCallback((id: string) => {
    setCaptureProgress(prev => { const next = { ...prev }; delete next[id]; return next; });
    setStaleCaptures(prev => { const next = new Set(prev); next.delete(id); return next; });
  }, []);
  // Post-connect confirmation popup. Set to the connected account email to show the modal.
  const [connectedAccount, setConnectedAccount] = useState<string | null>(null);
  // Track the previous source_account for each chat source to detect null → populated transitions.
  const prevSourceAccountRef = useRef<Record<string, string | null>>({});
  // Guards the connected-popup effect: the FIRST run only seeds the baseline (the surface
  // arrives already-populated for sources connected in a prior session), so the modal never
  // fires on a plain page mount — only on a genuine transition seen on a later poll.
  const connectedPopupSeededRef = useRef(false);
  // GitHub repo-selection picker: the required setup step opened right after a GitHub OAuth connect,
  // and the edit-later entry point from the card / Manage drawer.
  const [repoPickerOpen, setRepoPickerOpen] = useState(false);
  const [repoSaveLoading, setRepoSaveLoading] = useState(false);
  // Linear team-selection picker: the required setup step opened right after a Linear OAuth
  // connect, and the edit-later entry point from the card / Manage drawer.
  const [teamPickerOpen, setTeamPickerOpen] = useState(false);
  const [teamSaveLoading, setTeamSaveLoading] = useState(false);
  const queryClient = useQueryClient();
  const { open: openOAuthPopup } = useOAuthPopup();
  // Slack is a team-level connection — the connect URL is minted per team and admin-gated.
  const { data: team } = useTeamSettings();
  const teamId = team?.id;
  // bug-395: is the viewer a team admin? Team-shared sources (slack/notion) are admin-only to
  // connect/manage; non-admins get a read-only status card and the connect action no-ops (the
  // server-side gate already 403s, this just stops offering a dead button). Derived from the same
  // team members + current user id the TeamMembers surface uses.
  const { data: currentUserId } = useCurrentUserId();
  const isTeamAdmin = memberIsTeamAdmin(team?.members, currentUserId);

  const sources = buildSources(data.sites ?? [], data.integrations ?? [], { hasApiKey: data.has_api_key, capturePaused: data.capture_paused, devices: data.devices });
  const activeCount = sources.filter(s => s.status === 'active').length;
  const pausedCount = sources.filter(s => s.status === 'paused').length;
  const totalConvos = sources.reduce((sum, s) => sum + s.conversation_count, 0);

  // Live backfill progress from the extension (relayed via content-announce.js postMessages).
  // The SPA cannot read chrome.storage directly, so it asks the content script for a snapshot
  // and then receives incremental updates.
  useEffect(() => {
    function onMessage(event: MessageEvent) {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'terum-backfill-progress-update' && event.data.progress) {
        const p = event.data.progress;
        lastUpdateRef.current[p.site] = Date.now();
        setStaleCaptures(prev => { const next = new Set(prev); next.delete(p.site); return next; });
        setCaptureProgress(prev => ({ ...prev, [p.site]: { status: p.status, fetched: p.fetched ?? 0, estimated: p.estimated ?? 0, totalConversations: p.totalConversations ?? 0, conversationsProcessed: p.conversationsProcessed ?? 0, lastConversationTitle: p.lastConversationTitle ?? null } }));
        if (p.status === 'complete' || p.status === 'error') {
          queryClient.invalidateQueries({ queryKey: sourcesSurfaceQueryKey });
        }
      }
      if (event.data?.type === 'terum-backfill-progress-response' && event.data.data) {
        const all = event.data.data;
        const now = Date.now();
        // bug-203: a slow-but-live prod backfill (pages ~30s+ apart under Chrome's
        // packed-build alarm clamp) must not be discarded from a snapshot as stale.
        const STALE_PROGRESS_MS = 180_000;
        const next: Record<string, CaptureProgress> = {};
        for (const key of Object.keys(all)) {
          const p = all[key];
          if (!p?.site) continue;
          if (p.status === 'running' && (!p.lastUpdatedAt || now - p.lastUpdatedAt > STALE_PROGRESS_MS)) continue;
          next[p.site] = { status: p.status, fetched: p.fetched ?? 0, estimated: p.estimated ?? 0, totalConversations: p.totalConversations ?? 0, conversationsProcessed: p.conversationsProcessed ?? 0, lastConversationTitle: p.lastConversationTitle ?? null };
          if (p.status === 'running') lastUpdateRef.current[p.site] = Date.now();
        }
        if (Object.keys(next).length > 0) setCaptureProgress(prev => ({ ...prev, ...next }));
      }
    }
    window.addEventListener('message', onMessage);
    window.postMessage({ type: 'terum-get-backfill-progress' }, window.location.origin);
    return () => window.removeEventListener('message', onMessage);
  }, [queryClient]);

  // Heartbeat: if a source has been "running" with no live updates for a while, mark stale.
  // bug-203: in a packed build Chrome clamps the extension's next-page alarm to ~30s, so
  // healthy prod pages arrive ~30s+ apart (clamp + page work). 120s sits above the real
  // cadence so only a genuine stall trips it.
  useEffect(() => {
    const STALE_MS = 120_000;
    const timer = setInterval(() => {
      const now = Date.now();
      for (const [site, prog] of Object.entries(captureProgress)) {
        if (prog.status !== 'running') continue;
        const lastAt = lastUpdateRef.current[site] ?? 0;
        if (now - lastAt > STALE_MS) {
          setStaleCaptures(prev => new Set(prev).add(site));
        }
      }
    }, 5_000);
    return () => clearInterval(timer);
  }, [captureProgress]);

  // Detect chat source first-bind: when source_account transitions from null/absent →
  // populated in the polled surface data, fire the "now connected" modal once per source.
  // This covers the cookie-based connect path (chatgpt/claude/perplexity) where the email
  // is only known when the extension first reports identity after the connect POST.
  useEffect(() => {
    const prev = prevSourceAccountRef.current;
    const seeding = !connectedPopupSeededRef.current;
    for (const source of sources) {
      if (!CONVERSATION_SOURCES.includes(source.id)) continue;
      const email = source.source_account?.email ?? null;
      const prevEmail = prev[source.id] ?? null;
      // null → populated: show the modal once — but NOT on the first run (that just records
      // the already-connected baseline; firing there would pop the modal on every mount).
      if (!seeding && prevEmail === null && email !== null) {
        setConnectedAccount(email);
        track('source_connected', { source: source.id }); // funnel: chat source first-bind observed
      }
      prev[source.id] = email;
    }
    connectedPopupSeededRef.current = true;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.integrations]);

  // triggerBackfill + the connect POST now live in lib/connect-source (shared with
  // the onboarding chat branch). The connectChatSource call below reproduces the exact
  // network behavior — connect POST, then re-arm the extension backfill only when the
  // user opted into importing history. UI orchestration (loading/error/cache
  // invalidation) stays here.
  const connectSource = useCallback(async (sourceId: string, opts?: { consentFuture?: boolean; consentPast?: boolean }) => {
    try {
      setActionLoading(sourceId);
      setErrorMessage(null);
      await connectChatSource({
        sourceId: sourceId as 'chatgpt' | 'claude' | 'perplexity',
        consentFuture: opts?.consentFuture ?? true,
        consentPast: opts?.consentPast ?? false,
      });
      queryClient.invalidateQueries({ queryKey: sourcesSurfaceQueryKey });
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to connect source');
    } finally {
      setActionLoading(null);
    }
  }, [queryClient]);

  // Full revoke: disconnect + delete data. deleteData is always true for a Remove (the
  // keep/delete fork is gone — Pause keeps data, Remove deletes it).
  const disconnectSource = useCallback(async (sourceId: string) => {
    try {
      setRemoveLoading(true);
      setErrorMessage(null);
      await apiFetch('/api/surfaces/sources', { method: 'POST', body: JSON.stringify(buildSourceActionBody(sourceId, 'remove')) });
      queryClient.invalidateQueries({ queryKey: sourcesSurfaceQueryKey });
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to remove source');
    } finally {
      setRemoveLoading(false);
    }
  }, [queryClient]);

  // Explicit Remove: open the confirm. A user chose to remove an established source, so they
  // get a confirm (full revoke is destructive). req A: this is NOT the silent cancel-fresh
  // path — that one revokes directly with no popup.
  const removeSource = useCallback((sourceId: string) => {
    setRemovePending(sourceId);
  }, []);

  // Resolve the open Remove confirm.
  const confirmRemove = useCallback(async () => {
    if (!removePending) return;
    const id = removePending;
    await disconnectSource(id);
    setRemovePending(null);
    // The manage drawer stays open BEHIND the confirm now (so Cancel returns to it), but once the
    // source is actually gone there is nothing left to manage — close it on success only.
    setManagePending(null);
  }, [removePending, disconnectSource]);

  // Pause capture without disconnecting (keeps already-captured data). For chat sources this
  // just flips a flag; for OAuth sources the backend revokes the stored token — either way this
  // call is the same generic POST, the backend routes by provider.
  const pauseSource = useCallback(async (sourceId: string) => {
    try {
      setActionLoading(sourceId);
      setErrorMessage(null);
      await apiFetch('/api/surfaces/sources', { method: 'POST', body: JSON.stringify(buildSourceActionBody(sourceId, 'pause')) });
      queryClient.invalidateQueries({ queryKey: sourcesSurfaceQueryKey });
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to pause source');
      // The backend persists the status flip BEFORE the metadata patch (it must stop sync/sweep
      // pickup first), so a failure here can mean the source is already disconnected server-side
      // even though THIS request reports an error. Refetch so the UI reconciles to the real state
      // now instead of showing a stale "active" card until the next unrelated poll — mirrors
      // cancelImport's same-shaped partial-failure handling above.
      queryClient.invalidateQueries({ queryKey: sourcesSurfaceQueryKey });
    } finally {
      setActionLoading(null);
    }
  }, [queryClient]);

  // Explicit Pause: open the confirm (PauseConfirmDialog) rather than pausing immediately —
  // mirrors removeSource below, since an OAuth pause is a one-way door until the user reconnects.
  const requestPause = useCallback((sourceId: string) => {
    setPausePending(sourceId);
  }, []);

  // Resolve the open Pause confirm.
  const confirmPause = useCallback(async () => {
    if (!pausePending) return;
    const id = pausePending;
    // Await BEFORE clearing — mirrors confirmRemove below. Clearing first would unmount
    // PauseConfirmDialog on the same tick, so its own loading state (the disabled Cancel/X,
    // the "Pausing…" button text) could never actually render.
    await pauseSource(id);
    setPausePending(null);
  }, [pausePending, pauseSource]);

  // Resume a paused chat source. Triggers a gap backfill via the extension so
  // any history captured while paused is imported automatically — no consent modal.
  const reactivateSource = useCallback(async (sourceId: string) => {
    try {
      setActionLoading(sourceId);
      setErrorMessage(null);
      await apiFetch('/api/surfaces/sources', { method: 'POST', body: JSON.stringify(buildSourceActionBody(sourceId, 'reactivate')) });
      // Gap auto-fill: kick off a backfill to import history captured since the source was
      // paused. The pause flag is already cleared server-side, so a failed ack does NOT undo
      // the resume — surface a non-blocking notice instead of failing the whole action
      // (no-silent-degradation: don't claim success while importing nothing).
      const backfill = await triggerBackfill(sourceId);
      if (!backfill.ok) {
        setErrorMessage('Resumed, but couldn’t import history captured while paused — the Terum extension may be signed out.');
      }
      queryClient.invalidateQueries({ queryKey: sourcesSurfaceQueryKey });
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to reactivate source');
    } finally {
      setActionLoading(null);
    }
  }, [queryClient]);

  // Cancel an in-progress import run. Clears the optimistic progress state; if the backend
  // reports wasFresh (no data pre-dates this run), SILENTLY full-revokes the source — a
  // fresh connect has no old data to keep, so there's no Keep/Delete decision (req A).
  const cancelImport = useCallback(async (sourceId: string) => {
    try {
      setActionLoading(sourceId);
      setErrorMessage(null);
      const res = await apiFetch<{ paused: boolean; wasFresh: boolean }>(
        '/api/surfaces/sources',
        { method: 'POST', body: JSON.stringify(buildSourceActionBody(sourceId, 'cancel')) }
      );
      // Clear the optimistic capture progress regardless of wasFresh.
      clearCaptureState(sourceId);
      if (res?.wasFresh) {
        // No history predated this run — SILENTLY full-revoke so the source returns to
        // not-connected. No Remove popup: a fresh source has no old data to keep (req A).
        await disconnectSource(sourceId);
      }
      // Always reconcile to the real server state. disconnectSource swallows its own errors
      // (doesn't throw), so the wasFresh branch would otherwise leave the UI stale on a failed
      // revoke; invalidating unconditionally also covers the non-fresh (still-paused) branch.
      queryClient.invalidateQueries({ queryKey: sourcesSurfaceQueryKey });
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to cancel import');
      // The backend pauses BEFORE purging (it must stop the gate first), so the source is
      // paused server-side even when the purge half fails and returns 500. Clear the optimistic
      // CAPTURING bar and refetch so the UI reconciles to the real (paused) state now, instead
      // of showing stale progress + an error until the next poll.
      clearCaptureState(sourceId);
      queryClient.invalidateQueries({ queryKey: sourcesSurfaceQueryKey });
    } finally {
      setActionLoading(null);
    }
  }, [queryClient, disconnectSource, clearCaptureState]);

  // Top-right "Delete All": wipe everything + pause incoming capture (pauseCapture:true
  // re-asserts the pause server-side after the purge). All queries are invalidated.
  const deleteAllData = useCallback(async () => {
    try {
      setDeleteAllLoading(true);
      setErrorMessage(null);
      await apiFetch('/api/account/delete-data', { method: 'POST', body: JSON.stringify({ pauseCapture: true }) });
      setDeleteAllOpen(false);
      queryClient.invalidateQueries();
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to delete all data');
    } finally {
      setDeleteAllLoading(false);
    }
  }, [queryClient]);

  // Point the team briefing at an existing channel the bot already joined.
  const setTeamChannel = useCallback(async (sourceId: string, channelId: string) => {
    try {
      setActionLoading(sourceId);
      setErrorMessage(null);
      await apiFetch('/api/surfaces/sources', { method: 'POST', body: JSON.stringify({ sourceId, action: 'set-team-channel', channelId }) });
      queryClient.invalidateQueries({ queryKey: sourcesSurfaceQueryKey });
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to set briefing channel');
    } finally {
      setActionLoading(null);
    }
  }, [queryClient]);

  // Open the GitHub repo picker (from the card CTA or the Manage drawer). GitHub-only; the id is
  // ignored (there is a single GitHub source).
  const openRepoPicker = useCallback(() => setRepoPickerOpen(true), []);

  // Persist the chosen repos. The backend resolves them against the user's token, purges the
  // deselected repos' mirrored data, and fires the sync — the SPA just refetches on success.
  const setGithubRepos = useCallback(async (repoIds: number[]) => {
    try {
      setRepoSaveLoading(true);
      setErrorMessage(null);
      await apiFetch('/api/surfaces/sources', {
        method: 'POST',
        body: JSON.stringify({ sourceId: 'github', action: 'set-github-repos', repoIds }),
      });
      queryClient.invalidateQueries({ queryKey: sourcesSurfaceQueryKey });
      setRepoPickerOpen(false);
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to save repositories');
    } finally {
      setRepoSaveLoading(false);
    }
  }, [queryClient]);

  // Open the Linear team picker (from the card CTA or the Manage drawer). Linear-only; the id is
  // ignored (there is a single Linear source).
  const openTeamPicker = useCallback(() => setTeamPickerOpen(true), []);

  // Persist the chosen teams. The backend resolves them against the user's token, purges the
  // deselected teams' mirrored data, and fires the sync — the SPA just refetches on success.
  const setLinearTeams = useCallback(async (teamIds: string[]) => {
    try {
      setTeamSaveLoading(true);
      setErrorMessage(null);
      await apiFetch('/api/surfaces/sources', {
        method: 'POST',
        body: JSON.stringify({ sourceId: 'linear', action: 'set-linear-teams', teamIds }),
      });
      queryClient.invalidateQueries({ queryKey: sourcesSurfaceQueryKey });
      setTeamPickerOpen(false);
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to save teams');
    } finally {
      setTeamSaveLoading(false);
    }
  }, [queryClient]);

  // Manual RETRY / partial re-load: re-arm the extension backfill and show optimistic
  // progress — but only AFTER the extension acks (bug-207). If it doesn't ack, clear the
  // optimistic bar and show the honest reason rather than a CAPTURING state importing nothing.
  const handleBackfill = useCallback(async (sourceId: string) => {
    // Re-import runs through the desktop extension handshake (triggerBackfill), which can't
    // run on a phone — route to the desktop sheet instead of firing a handshake that errors.
    if (isMobile) { setDesktopSheet({ open: true, source: sourceName(sourceId) }); return; }
    setErrorMessage(null);
    setStaleCaptures(prev => { const next = new Set(prev); next.delete(sourceId); return next; });
    lastUpdateRef.current[sourceId] = Date.now();
    setCaptureProgress(prev => ({
      ...prev,
      [sourceId]: { status: 'running', fetched: 0, estimated: 0, totalConversations: 0, conversationsProcessed: 0, lastConversationTitle: null },
    }));
    const result = await triggerBackfill(sourceId);
    if (!result.ok) {
      clearCaptureState(sourceId);
      setErrorMessage(
        result.error === 'not_connected'
          ? 'The Terum extension isn’t signed in — reconnect it, then try again.'
          : 'Couldn’t reach the Terum extension — make sure it’s installed and connected, then try again.'
      );
      return;
    }
    // The extension acked and triggerBackfill wrote the durable backfill_started_at marker
    // server-side. Invalidate so the query refetches, picks up backfill_in_progress=true, and
    // ARMS the poll-while-running (useSourcesSurface refetchInterval). Only invalidate on
    // result.ok (a failed ack changed no server state).
    queryClient.invalidateQueries({ queryKey: sourcesSurfaceQueryKey });
  }, [queryClient, clearCaptureState, isMobile]);

  // SKIP IMPORT on a stalled backfill: mark it complete server-side so the card stops nagging.
  const skipImport = useCallback(async (sourceId: string) => {
    try {
      setActionLoading(sourceId);
      clearCaptureState(sourceId);
      await apiFetch('/api/surfaces/sources', { method: 'POST', body: JSON.stringify({ sourceId, action: 'force-complete' }) });
      queryClient.invalidateQueries({ queryKey: sourcesSurfaceQueryKey });
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to skip import');
    } finally {
      setActionLoading(null);
    }
  }, [queryClient, clearCaptureState]);

  // Google Resync: POST the server-side resync action (kicks off a Google sync server-side,
  // records resync_requested_at). Must NOT use triggerBackfill — Google is OAuth-only, has no
  // extension, and triggerBackfill always errors "the Terum extension isn't signed in" for it.
  const resyncSource = useCallback(async (sourceId: string) => {
    try {
      setActionLoading(sourceId);
      setErrorMessage(null);
      await apiFetch('/api/surfaces/sources', { method: 'POST', body: JSON.stringify({ sourceId, action: 'resync' }) });
      queryClient.invalidateQueries({ queryKey: sourcesSurfaceQueryKey });
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to resync source');
    } finally {
      setActionLoading(null);
    }
  }, [queryClient]);

  // bug-171: resolve a source-account drift via a single Connect (re-consent the new account
  // → rebind + auto-backfill). This handles the picker's FIRST verb, 'rebind' — accept the
  // now-logged-in account as the new binding. Unit A restores the second verb via
  // keepBoundAccount below — D13's rebind-only ruling is superseded.
  const resolveDrift = useCallback(async (sourceId: string, email?: string) => {
    try {
      setActionLoading(sourceId);
      setErrorMessage(null);
      await apiFetch('/api/integrations/source-identity', {
        method: 'PATCH',
        body: JSON.stringify({ provider: sourceId, action: 'rebind' }),
      });
      // Re-arm the backfill so it imports under the now-bound identity (the extension's next
      // report will match, not drift). The rebind already succeeded server-side, so a failed
      // ack does NOT undo it — surface a non-blocking notice (no-silent-degradation), mirroring
      // reactivateSource.
      const backfill = await triggerBackfill(sourceId);
      if (!backfill.ok) {
        setErrorMessage('Connected, but couldn’t import history for the newly-connected account — the Terum extension may be signed out.');
      }
      // Show the "now connected" modal for the newly-bound account when the email is known.
      if (email) setConnectedAccount(email);
      setReconnectPending(null);
      queryClient.invalidateQueries({ queryKey: sourcesSurfaceQueryKey });
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to resolve account change');
    } finally {
      setActionLoading(null);
    }
  }, [queryClient]);

  // The picker's SECOND verb (unit A): keep the bound account. The server clears the drift flag
  // WITHOUT rebinding, so we stop nagging while capture stays paused until the bound account is
  // signed back in (its next matching identity report clears everything and resumes capture).
  // No backfill re-arm here — nothing was rebound, so there is no new identity to import under.
  const keepBoundAccount = useCallback(async (sourceId: string) => {
    try {
      setActionLoading(sourceId);
      setErrorMessage(null);
      await apiFetch('/api/integrations/source-identity', {
        method: 'PATCH',
        body: JSON.stringify({ provider: sourceId, action: 'ignore' }),
      });
      // Close the picker on success, same as the rebind branch — the drift is resolved, so
      // leaving it open would keep showing copy about a choice that has already been made.
      setReconnectPending(null);
      queryClient.invalidateQueries({ queryKey: sourcesSurfaceQueryKey });
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to save that choice');
    } finally {
      setActionLoading(null);
    }
  }, [queryClient]);

  const requestConnect = useCallback((sourceId: string) => {
    // Mobile has no extension and can't host the OAuth popup — redirect to desktop instead
    // of opening any drawer/popup. This single guard covers every fresh connect + resume
    // (handleActivate, resumeOAuthSource) and the drawers they open (consent, setup, slack DMs).
    if (isMobile) { setDesktopSheet({ open: true, source: sourceName(sourceId) }); return; }
    // bug-395: team-shared sources (slack/notion) are admin-gated server-side, so a non-admin
    // connect can only ever 403. The card already renders a read-only status for non-admins (no
    // Activate), so this is defense-in-depth: never open the popup for a member — surface why.
    if (TEAM_SHARED_OAUTH_SOURCES.includes(sourceId) && !isTeamAdmin) {
      setErrorMessage(`Only Terum team admins can connect ${sourceName(sourceId)}.`);
      return;
    }
    // Shared Google/Slack OAuth popup connect: identical popup + cookie-binding contract
    // (CWE-598 / bug-091 — no JWT in the URL, auth rides the Authorization header; the popup
    // opens synchronously inside the click, then navigates straight to the provider). The two
    // differ only by endpoint + label; the Slack teamId guard stays per-source below.
    const openOAuthConnect = (urlPath: string, label: string, onSuccess?: () => void) => {
      setErrorMessage(null);
      openOAuthPopup(
        async () => {
          const { url } = await apiFetch<{ url: string }>(urlPath, { credentials: 'include' });
          return url;
        },
        (payload) => {
          if (!payload.ok) { setErrorMessage(payload.error || `Failed to connect ${label}`); return; }
          track('source_connected', { source: sourceId }); // funnel: OAuth popup reported success
          // A source with a distinct post-connect step (GitHub → pick repositories) runs it here;
          // otherwise (Google/Slack) confirm the OAuth connect against the signed-in account (req B),
          // since those have no per-source source_account.email.
          if (onSuccess) { onSuccess(); return; }
          if (userEmail) setConnectedAccount(userEmail);
        }
      );
    };
    if (sourceId === 'github') {
      // GitHub is a per-USER OAuth source (like Google) — no team-admin gate, no teamId. On success,
      // open the repo picker: connecting only stores the token; nothing is ingested until the user
      // chooses which repositories to mirror (the required setup step).
      openOAuthConnect('/api/surfaces/sources/github-connect-url', 'GitHub', () => setRepoPickerOpen(true));
      return;
    }
    if (sourceId === 'google') {
      openOAuthConnect('/api/surfaces/sources/google-connect-url', 'Google');
      return;
    }
    if (sourceId === 'linear') {
      // Linear is a per-USER OAuth source (no team-admin gate, no teamId). On success, open the team
      // picker: connecting only stores the token; nothing is ingested until the user chooses which
      // teams to mirror (the required setup step, mirroring GitHub's repo picker).
      openOAuthConnect('/api/surfaces/sources/linear-connect-url', 'Linear', () => setTeamPickerOpen(true));
      return;
    }
    if (sourceId === 'slack') {
      // The connect URL is admin-gated server-side, so a non-admin caller gets a 403 here.
      if (!teamId) {
        setErrorMessage('Create or join a team before connecting Slack.');
        return;
      }
      openOAuthConnect(`/api/surfaces/sources/slack-connect-url?teamId=${encodeURIComponent(teamId)}`, 'Slack');
      return;
    }
    if (sourceId === 'notion') {
      // Notion is team-scoped + admin-gated server-side (the workspace token is team-wide), so a
      // teamId is required and a non-admin caller gets a 403 from the connect-url route. Mirrors
      // the Slack branch exactly.
      if (!teamId) {
        setErrorMessage('Create or join a team before connecting Notion.');
        return;
      }
      openOAuthConnect(`/api/surfaces/sources/notion-connect-url?teamId=${encodeURIComponent(teamId)}`, 'Notion');
      return;
    }
    if (sourceId === 'slack_user') {
      // tier-3 is member-level (any role), but the connect-url BFF route is team-membership-
      // gated, so a teamId is still required. Open the DEC-1b disclosure drawer first; the
      // OAuth popup is opened from its confirm click (a user gesture), not here.
      if (!teamId) {
        setErrorMessage('Join a team before connecting your Slack DMs.');
        return;
      }
      setErrorMessage(null);
      setSlackDmConsentOpen(true);
      return;
    }
    if (sourceId === 'claude-code') {
      setSetupOpen(true);
      return;
    }
    if (sourceId === 'granola') {
      // Granola connects with a pasted API key (no OAuth popup, no extension) — open the key
      // drawer; the validated POST fires from its confirm.
      setErrorMessage(null);
      setGranolaConnectOpen(true);
      return;
    }
    setConsentPending(sourceId);
  }, [openOAuthPopup, teamId, userEmail, isMobile, isTeamAdmin]);

  // Open the tier-3 OAuth popup after the DEC-1b disclosure is acknowledged. Mirrors the
  // Google/Slack popup contract (popup opened synchronously on the click gesture, connect URL
  // minted via an authenticated fetch, no JWT in the URL — CWE-598). slack_user has no per-source
  // source_account.email, so confirm against the signed-in account on success (req B).
  const connectSlackDms = useCallback(() => {
    // Validate BEFORE closing the disclosure drawer — otherwise the drawer closes and the error
    // surfaces against an empty page. Returning early here keeps the drawer open so the team
    // requirement reads in context.
    if (!teamId) {
      setErrorMessage('Join a team before connecting your Slack DMs.');
      return;
    }
    setSlackDmConsentOpen(false);
    setErrorMessage(null);
    openOAuthPopup(
      async () => {
        const { url } = await apiFetch<{ url: string }>(
          `/api/surfaces/sources/slack-personal-connect-url?teamId=${encodeURIComponent(teamId)}`,
          { credentials: 'include' },
        );
        return url;
      },
      (payload) => {
        if (!payload.ok) { setErrorMessage(payload.error || 'Failed to connect Slack DMs'); return; }
        track('source_connected', { source: 'slack_user' }); // funnel: tier-3 DM connect succeeded
        if (userEmail) setConnectedAccount(userEmail);
      },
    );
  }, [openOAuthPopup, teamId, userEmail]);

  // Connect Granola with a pasted API key. Returns a result so the drawer can show the failure
  // inline (where the user's attention is) rather than on the page — the drawer stays open on a
  // bad key. The BFF validates the key BEFORE persisting: a 400 means the key itself is wrong,
  // anything else is transient. Branch on the structured ApiError status, never string-match.
  const connectGranola = useCallback(async (apiKey: string): Promise<{ ok: boolean; error?: string }> => {
    try {
      await apiFetch('/api/surfaces/sources/granola-connect', { method: 'POST', body: JSON.stringify({ apiKey }) });
      setGranolaConnectOpen(false);
      queryClient.invalidateQueries({ queryKey: sourcesSurfaceQueryKey });
      track('source_connected', { source: 'granola' }); // funnel: validated Granola key persisted
      return { ok: true };
    } catch (err) {
      const msg = err instanceof ApiError && err.status === 400
        ? 'That Granola API key looks invalid — double-check it and try again.'
        : (err instanceof Error ? err.message : 'Failed to connect Granola');
      return { ok: false, error: msg };
    }
  }, [queryClient]);

  const confirmConsent = useCallback((consentFuture: boolean, consentPast: boolean) => {
    if (consentPending) {
      const id = consentPending;
      setConsentPending(null);
      connectSource(id, { consentFuture, consentPast });
    }
  }, [consentPending, connectSource]);

  // Activate a not-connected source. Chat sources open the d-connect drawer (which folds in
  // the D13 "add the extension" step when the extension isn't connected); Google/Slack open
  // their OAuth popup; Claude Code opens its install drawer.
  const handleActivate = useCallback((sourceId: string) => {
    track('source_connect_started', { source: sourceId }); // funnel: user began connecting a source
    requestConnect(sourceId);
  }, [requestConnect]);

  // Resume a paused OAuth source: there's no token left to re-arm (Pause revoked it), so
  // resuming is the same connect flow as a fresh Activate. Close the Manage drawer first so
  // the OAuth popup isn't hidden behind it.
  const resumeOAuthSource = useCallback((sourceId: string) => {
    setManagePending(null);
    requestConnect(sourceId);
  }, [requestConnect]);

  // Deep-link from the Manage drawer to the Knowledge list (/map · List), filtered to this
  // source's site. Close the drawer first so the user lands on the Map, not back on Sources.
  const viewInKnowledge = useCallback((sourceId: string) => {
    const view = KNOWLEDGE_VIEW[sourceId];
    if (!view) return;
    setManagePending(null);
    setDevicesPending(null);
    onNav(`map?view=list&source=${view.site}`);
  }, [onNav]);

  // Manage routes claude-code to its own Devices drawer (key management); every other
  // source uses the generic ManageDrawer.
  const handleManage = useCallback((sourceId: string) => {
    // Safety net: management (incl. disconnect/delete) is desktop-only. The Manage button is
    // already hidden on mobile, so this only fires if a future caller reaches here on a phone —
    // point them at the desktop app rather than open a destructive drawer.
    if (isMobile) { setDesktopSheet({ open: true, source: sourceName(sourceId) }); return; }
    if (sourceId === 'claude-code') setDevicesPending(sourceId);
    else setManagePending(sourceId);
  }, [isMobile]);

  // Revoke ONE Claude Code device (terum-capture machine). Scoped server-side to the user
  // (IDOR guard); that machine stops syncing until `terum-capture setup` is run again. Uses
  // actionLoading keyed by the keyId so only the clicked row shows "Revoking…".
  const revokeDevice = useCallback(async (keyId: string) => {
    try {
      setActionLoading(keyId);
      setErrorMessage(null);
      await apiFetch('/api/surfaces/sources', {
        method: 'POST',
        body: JSON.stringify({ sourceId: 'claude-code', action: 'revoke-device', keyId }),
      });
      queryClient.invalidateQueries({ queryKey: sourcesSurfaceQueryKey });
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to revoke device');
    } finally {
      setActionLoading(null);
    }
  }, [queryClient]);

  // Delete all captured Claude Code data — independent of device revocation (decision D).
  const deleteClaudeCodeData = useCallback(async () => {
    try {
      setDeleteDataLoading(true);
      setErrorMessage(null);
      await apiFetch('/api/surfaces/sources', {
        method: 'POST',
        body: JSON.stringify({ sourceId: 'claude-code', action: 'delete-data' }),
      });
      setDeleteDataOpen(false);
      queryClient.invalidateQueries({ queryKey: sourcesSurfaceQueryKey });
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to delete Claude Code data');
    } finally {
      setDeleteDataLoading(false);
    }
  }, [queryClient]);

  // Pre-compute capture state once per source — the sort comparator + the per-card render
  // both read it, so the predicate runs once.
  const capturingById = new Map(sources.map(s => {
    const cp = captureProgress[s.id] ?? null;
    const isConv = CONVERSATION_SOURCES.includes(s.id);
    return [s.id, deriveCapturing(s, cp, staleCaptures.has(s.id), isConv)] as const;
  }));

  const gridSources = [...sources].sort(
    (a, b) => sourceSortRank(a, capturingById.get(a.id) ?? false) - sourceSortRank(b, capturingById.get(b.id) ?? false)
  );

  const manageSource = managePending ? sources.find(s => s.id === managePending) ?? null : null;
  const reconnectSource = reconnectPending ? sources.find(s => s.id === reconnectPending) ?? null : null;
  const devicesSource = devicesPending ? sources.find(s => s.id === devicesPending) ?? null : null;

  const extInstalled = extStatus === 'connected';
  const subline = activeCount > 0 || pausedCount > 0
    ? `${activeCount} active${pausedCount > 0 ? ` · ${pausedCount} paused` : ''} · ${totalConvos.toLocaleString()} captured`
    : 'Connect your AI tools to start building your shared context layer.';

  return (
    <div className="v2-content rd p-0 bg-background font-['DM_Sans'] text-foreground">
      <div className="mx-auto max-w-[1500px] px-4 pb-16 pt-4 sm:px-7 lg:px-10">
        <PageMast
          eyebrow="Sources · observe"
          title={<>Connect once. It <em>flows from here.</em></>}
          intro="The tools this team already works in, each captured independently — pausing one never touches another. Nothing here reports on anyone; what a teammate marks private stays private."
          tiles={<>
            <MastTile
              label="ACTIVE"
              value={activeCount}
              note={pausedCount > 0 ? `${pausedCount} paused by you` : activeCount > 0 ? 'all flowing' : 'nothing connected yet'}
            />
            <MastTile label="CAPTURED" value={totalConvos.toLocaleString()} note="conversations distilled into notes" />
          </>}
        />

        {/* Status strip: extension + delete-all. The subline keeps the honest rollup. */}
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <span className="flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-[13px]">
            <span className={`size-1.5 rounded-full ${extInstalled ? 'bg-[#62725c]' : 'bg-[#b95c4b]'}`} />
            Chrome extension · {extInstalled ? 'connected' : extStatus === 'error' ? 'not connected' : 'not installed'}
          </span>
          {extStatus === 'not_installed' && (
            <a className="rounded-full border border-[#d7cab7] bg-[#fffaf2] px-3 py-1.5 text-[13px] transition hover:border-foreground dark:border-border dark:bg-card" href={CHROME_STORE_URL} target="_blank" rel="noopener noreferrer">Add to Chrome</a>
          )}
          {extStatus === 'error' && (
            <button className="rounded-full border border-[#d7cab7] bg-[#fffaf2] px-3 py-1.5 text-[13px] transition hover:border-foreground dark:border-border dark:bg-card" onClick={() => retryExtension()}>Reconnect</button>
          )}
          <span className="text-[12px] text-muted-foreground">{subline}</span>
          {/* Destructive kill switch — desktop-only, like every other management action. */}
          {!isMobile && (
            <button
              className="ml-auto rounded-full border border-[#e2aa9c] px-3 py-1.5 text-[13px] text-[#b95c4b] transition hover:bg-[#fdf0e7] dark:hover:bg-destructive/10"
              onClick={() => setDeleteAllOpen(true)}
            >
              Delete all data
            </button>
          )}
        </div>

        {errorMessage && (
          <div ref={errorBannerRef} className="mb-4 rounded-2xl border border-[#e2aa9c] bg-[#fdf0e7] px-4 py-3 text-[13px] text-[#8c5040] dark:border-destructive/40 dark:bg-destructive/10 dark:text-destructive">
            {errorMessage}
          </div>
        )}

        {/* Capture is paused only via "Delete All" now. Surface it so the paused state is never
            silent — reconnecting any source clears it server-side. */}
        {data.capture_paused && (
          <div className="mb-4 rounded-2xl border border-[#e0c9a0] bg-[#f9f0dc] px-4 py-3 text-[13px] text-[#8a6a33] dark:bg-transparent dark:text-[#c99b52]">
            Capture is paused — no new data is being recorded. Reconnect a source below to resume.
          </div>
        )}

        {/* Ghost Phase B (§3.2): the coverage strip — say what Terum can already see (N connected, M
            observed) so a partially-connected team reads as "not fully connected yet," never "broken."
            When the viewer is the only connector, name the team-shared-observed people (§3.3) and nudge
            an invite. Renders only when there is observed activity. Copy approved (Ryan, 2026-07-20). */}
        {data.coverage && data.coverage.observed > 0 && (
          <div className="mb-4 rounded-2xl border border-border bg-card px-4 py-3 text-[13px]">
            <div className="font-medium">
              {data.coverage.connected} connected · {data.coverage.observed} observed
            </div>
            {data.coverage.connected <= 1 && (
              <div className="mt-1 text-muted-foreground">
                You're the only one connected — invite teammates to unlock team sections.
                {data.coverage.observedNames.length > 0 && (
                  <span className="mt-1 block">
                    {data.coverage.observedNames.slice(0, 8).join(', ')}
                    {data.coverage.observed > data.coverage.observedNames.length
                      ? ` +${data.coverage.observed - data.coverage.observedNames.length} more`
                      : ''}
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Extension detail note (D12: chat-capture dependency, not a global gate). */}
        {!extInstalled && (
          <p className="mb-4 text-[12px] leading-5 text-muted-foreground">
            The extension is required to capture ChatGPT, Claude &amp; Perplexity. Google, Slack &amp; Claude Code connect on their own.
          </p>
        )}

        <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {gridSources.map(source => (
            <SourceCard
              key={source.id}
              source={source}
              captureProgress={captureProgress[source.id] ?? null}
              stale={staleCaptures.has(source.id)}
              isConversationSource={CONVERSATION_SOURCES.includes(source.id)}
              capturing={capturingById.get(source.id) ?? false}
              actionLoading={actionLoading}
              onManage={handleManage}
              onActivate={handleActivate}
              onReconnect={setReconnectPending}
              onSetTeamChannel={setTeamChannel}
              onChooseRepos={openRepoPicker}
              onChooseTeams={openTeamPicker}
              isMobile={isMobile}
              isTeamAdmin={isTeamAdmin}
            />
          ))}
        </section>
      </div>

      <ConnectDrawer
        sourceId={consentPending}
        extStatus={extStatus}
        onRetryExt={retryExtension}
        onConfirm={confirmConsent}
        onClose={() => setConsentPending(null)}
      />
      <SlackDmConnectDrawer
        open={slackDmConsentOpen}
        onConnect={connectSlackDms}
        onClose={() => setSlackDmConsentOpen(false)}
      />
      <GranolaConnectDrawer
        open={granolaConnectOpen}
        onConnect={connectGranola}
        onClose={() => setGranolaConnectOpen(false)}
      />
      <ManageDrawer
        source={manageSource}
        captureProgress={manageSource ? (captureProgress[manageSource.id] ?? null) : null}
        stale={manageSource ? staleCaptures.has(manageSource.id) : false}
        capturing={manageSource ? (capturingById.get(manageSource.id) ?? false) : false}
        actionLoading={actionLoading}
        removeLoading={removeLoading}
        onRequestPause={requestPause}
        onResume={reactivateSource}
        onReconnectPaused={resumeOAuthSource}
        onResync={resyncSource}
        onChooseRepos={openRepoPicker}
        onChooseTeams={openTeamPicker}
        onBackfill={handleBackfill}
        onSkip={skipImport}
        onCancelImport={cancelImport}
        onRemove={removeSource}
        onView={viewInKnowledge}
        onClose={() => setManagePending(null)}
      />
      <DevicesDrawer
        source={devicesSource}
        actionLoading={actionLoading}
        deleteLoading={deleteDataLoading}
        onRevokeDevice={revokeDevice}
        onDeleteData={() => setDeleteDataOpen(true)}
        onAddDevice={() => {
          // On mobile this is a dead end anyway, so close the drawer and explain. On desktop, keep
          // the Devices drawer MOUNTED behind the setup guide: "Add a device" is a step of device
          // management, and closing it first meant Done landed the user on the bare Sources grid
          // instead of back in the device list they were working through.
          if (isMobile) { setDevicesPending(null); setDesktopSheet({ open: true, source: sourceName('claude-code') }); return; }
          setSetupOpen(true);
        }}
        onView={viewInKnowledge}
        onClose={() => setDevicesPending(null)}
      />
      {/* AFTER DevicesDrawer on purpose: both are `Drawer`s at the same z-index, so DOM order
          decides which paints on top. "Add a device" opens this over the device list and leaves it
          mounted underneath, so Done returns there instead of dumping the user on the Sources grid. */}
      <SetupDrawer open={setupOpen} onClose={() => setSetupOpen(false)} />
      <ClaudeCodeDeleteDataDialog
        open={deleteDataOpen}
        loading={deleteDataLoading}
        onConfirm={deleteClaudeCodeData}
        onCancel={() => setDeleteDataOpen(false)}
      />
      <AccountPickerDrawer
        source={reconnectSource}
        loading={reconnectSource ? actionLoading === reconnectSource.id : false}
        onCapture={resolveDrift}
        onKeep={keepBoundAccount}
        onClose={() => setReconnectPending(null)}
      />
      <RemoveConfirmDialog
        sourceId={removePending}
        loading={removeLoading}
        onConfirm={confirmRemove}
        onCancel={() => setRemovePending(null)}
      />
      <PauseConfirmDialog
        sourceId={pausePending}
        loading={pausePending ? actionLoading === pausePending : false}
        onConfirm={confirmPause}
        onCancel={() => setPausePending(null)}
      />
      <DeleteAllDialog
        open={deleteAllOpen}
        loading={deleteAllLoading}
        onConfirm={deleteAllData}
        onCancel={() => setDeleteAllOpen(false)}
      />
      {connectedAccount && (
        <ConnectedAccountDialog email={connectedAccount} onClose={() => setConnectedAccount(null)} />
      )}
      <GithubRepoPicker
        open={repoPickerOpen}
        onClose={() => setRepoPickerOpen(false)}
        onSave={setGithubRepos}
        saving={repoSaveLoading}
      />
      <LinearTeamPicker
        open={teamPickerOpen}
        onClose={() => setTeamPickerOpen(false)}
        onSave={setLinearTeams}
        saving={teamSaveLoading}
      />
      <ConnectOnDesktopSheet
        open={desktopSheet.open}
        sourceName={desktopSheet.source}
        onClose={() => setDesktopSheet({ open: false })}
      />
    </div>
  );
}
